<template>
    <div class="h-100 w-100 d-flex flex-column">

        <div class="p-3">  <!-- Top Settings Area -->

            <div 
                class="rounded shadow px-3 py-2 overflow-hidden"
                :class="[settingsEditActive ? 'bg-gray-50 border shadow' : 'bg-gray-100']"
            >
                <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0 text-primary-darker text-uppercase">Settings</h6>
                    <div v-if="settingsEditActive" key="settingsEditActiveOn">
                        <b-button variant="secondary" size="sm" class="mr-2 px-3" @click="cancelColumnsEdit">Cancel</b-button>
                        <b-button :disabled="missingNameAndPartNumber" variant="primary" size="sm" class="px-4" @click="finishColumnsEdit">Update</b-button>
                    </div>
                    <b-button 
                        v-else 
                        key="settingsEditActiveOff" 
                        variant="secondary" 
                        size="sm" 
                        class="px-4" 
                        :disabled="disableChanges"
                        @click="startColumnsEdit"
                    >Edit</b-button>
                    
                </div>
                <hr class="mt-2">
                <div 
                    class="row"
                >
                    <div :class="settingsEditActive ? 'col-12' : 'col'">
                        <div class="row" :class="settingsEditActive ? 'py-3' : ''">
                            <div :class="settingsEditActive ? 'col-lg-3' : 'col-12'">
                                <h6 :class="settingsEditActive ? '' : 'text-primary-darker'">Title & Instructions</h6>
                                <div v-if="settingsEditActive">
                                    <p class="mb-2 text-muted small">
                                        This information will be visible to the vendors when submitting their responses.
                                    </p>
                                </div>
                            </div>
                            <div class="col">
                                <div v-if="settingsEditActive">
                                    <hot-edit-input
                                        label="Quote Sheet Title"
                                        v-model="quoteSheetData.title"
                                        placeholder="Quote Sheet"
                                        :loading="loading"
                                        :edit-mode="settingsEditActive"
                                        no-edit-class="h3"
                                        required
                                        label-align="left"
                                        label-vertical
                                        class="mb-3 flex-fill"
                                    ></hot-edit-input>
                                    <hot-edit-textarea
                                        label="Vendor Instructions"
                                        v-model="quoteSheetData.instructions"
                                        placeholder="(No instructions provided)"
                                        :loading="loading"
                                        :edit-mode="settingsEditActive"
                                        label-align="left"
                                        label-vertical
                                        class="mb-3"
                                    ></hot-edit-textarea>
                                </div>
                                <div v-else>
                                    <b-skeleton-wrapper :loading="loading">
                                        <template #loading>
                                            <b-skeleton height="1.5rem" width="25%"></b-skeleton>
                                            <b-skeleton width="85%"></b-skeleton>
                                            <b-skeleton width="45%"></b-skeleton>
                                        </template>
                                        <h3>{{ quoteSheetData.title }}</h3>
                                        <text-expandable 
                                            class="mb-0 text-muted"
                                            :class="quoteSheetData.instructions ? '' : 'text-muted font-italic'"
                                            :word-count="20"
                                            :content="quoteSheetData.instructions ? quoteSheetData.instructions : 'No instructions provided'"
                                        ></text-expandable>
                                    </b-skeleton-wrapper>
                                </div>
                            </div>
                        </div>
                    </div>

                    <hr v-if="settingsEditActive">
                    
                    <div :class="settingsEditActive ? 'col-12' : 'col'">
                        <div class="row" :class="settingsEditActive ? 'py-3' : ''">
                            <div :class="settingsEditActive ? 'col-lg-3' : 'col-12'">
                                <h6 :class="settingsEditActive ? '' : 'text-primary-darker'">Columns to Show</h6>
                                <div v-if="settingsEditActive">
                                    <p class="mb-2 text-muted small">
                                        Select which columns to show for each item.  Deselect to hide any columns that are not applicable for your event.
                                    </p>
                                </div>
                                <p v-else class="small text-muted">Select which columns to show for each item.</p>
                            </div>
                            <div class="col">
                                <div class="row">
                                    <div :class="settingsEditActive ? 'col-auto' : 'col-12'">
                                        <div class="text-uppercase small font-weight-bold" :class="settingsEditActive ? '' : 'text-muted'">Item Details</div>
                                        <div v-if="missingNameAndPartNumber" class="small text-danger font-weight-500">
                                            Invalid Item Details - must have either Item Name and/or Part Number showing
                                        </div>
                                        <div v-if="settingsEditActive">
                                            <div class="text-muted small mb-1">Descriptive information about each item.</div>
                                            <b-form-checkbox
                                                v-model="quoteSheetData.internalCols.show_name"
                                                class="mr-3"
                                            >
                                                Item Name
                                                <span class="text-muted small">(required if not showing Part Number)</span>
                                            </b-form-checkbox>
                                            <b-form-checkbox
                                                v-model="quoteSheetData.internalCols.show_part_number"
                                            >
                                                Part Number
                                                <span class="text-muted small">(required if not showing Item Name)</span>
                                            </b-form-checkbox>
                                            <b-form-checkbox
                                                v-model="quoteSheetData.internalCols.show_description"
                                            >Item Description</b-form-checkbox>
                                            <b-form-checkbox
                                                v-model="quoteSheetData.internalCols.show_need_by_date"
                                            >Need by Date</b-form-checkbox>
                                        </div>
                                        <div v-else class="d-flex flex-wrap my-1">
                                            <div class="qsheet-col-chip" :class="quoteSheetData.internalCols.show_name ? 'qsheet-col-on' : 'qsheet-col-off'">Item Name</div>
                                            <div class="qsheet-col-chip" :class="quoteSheetData.internalCols.show_part_number ? 'qsheet-col-on' : 'qsheet-col-off'">Part Number</div>
                                            <div class="qsheet-col-chip" :class="quoteSheetData.internalCols.show_description ? 'qsheet-col-on' : 'qsheet-col-off'">Description</div>
                                            <div class="qsheet-col-chip" :class="quoteSheetData.internalCols.show_need_by_date ? 'qsheet-col-on' : 'qsheet-col-off'">Need by Date</div>
                                        </div>
                                    </div>
                                    <div :class="settingsEditActive ? 'col-auto' : 'col-auto'">
                                        <div class="text-uppercase small font-weight-bold mr-3" :class="settingsEditActive ? '' : 'text-muted'">Quantities</div>
                                        <div v-if="settingsEditActive">
                                            <div class="text-muted small mb-1">Column related to purchase quantity.</div>
                                            <b-form-checkbox
                                                v-model="quoteSheetData.internalCols.show_quantity"
                                                class="mr-3"
                                            >Quantity</b-form-checkbox>
                                            <b-form-checkbox
                                                v-model="quoteSheetData.internalCols.show_uom"
                                                class="mr-3"
                                            >UoM <span class="small text-muted">(Unit of Measure)</span></b-form-checkbox>
                                            <b-form-checkbox
                                                v-model="quoteSheetData.internalCols.show_timeframe"
                                            >Timeframe</b-form-checkbox>
                                        </div>
                                        <div v-else class="d-flex flex-wrap my-1">
                                            <div class="qsheet-col-chip" :class="quoteSheetData.internalCols.show_quantity ? 'qsheet-col-on' : 'qsheet-col-off'">Quantity</div>
                                            <div class="qsheet-col-chip" :class="quoteSheetData.internalCols.show_uom ? 'qsheet-col-on' : 'qsheet-col-off'">Unit of Measure</div>
                                            <div class="qsheet-col-chip" :class="quoteSheetData.internalCols.show_timeframe ? 'qsheet-col-on' : 'qsheet-col-off'">Timeframe</div>
                                        </div>
                                    </div>
                                    <div :class="settingsEditActive ? 'col-auto' : 'col-auto'">
                                        <div class="text-uppercase small font-weight-bold mr-3" :class="settingsEditActive ? '' : 'text-muted'">Reference Prices</div>
                                        <div v-if="settingsEditActive">
                                            <div class="text-muted small mb-1">Internal reference prices (not shown to vendors)</div>
                                            <b-form-checkbox
                                                v-model="quoteSheetData.internalCols.show_current_price"
                                                class="mr-3"
                                            >Current Price</b-form-checkbox>
                                            <b-form-checkbox
                                                v-model="quoteSheetData.internalCols.show_target_price"
                                                class="mr-3"
                                            >Target Price</b-form-checkbox>
                                        </div>
                                        <div v-else class="d-flex flex-wrap my-1">
                                            <div class="qsheet-col-chip" :class="quoteSheetData.internalCols.show_current_price ? 'qsheet-col-on' : 'qsheet-col-off'">Current Price</div>
                                            <div class="qsheet-col-chip" :class="quoteSheetData.internalCols.show_target_price ? 'qsheet-col-on' : 'qsheet-col-off'">Target Price</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <hr v-if="settingsEditActive">

                    <div :class="settingsEditActive ? 'col-12' : 'col'">
                        <div class="row" :class="settingsEditActive ? 'py-3' : ''">
                            <div :class="settingsEditActive ? 'col-lg-3' : 'col-12'">
                                <h6 :class="settingsEditActive ? '' : 'text-primary-darker'">
                                    Vendor Response Data
                                    <b-badge v-if="choicesInvalid" variant="danger" class="ml-2">Error</b-badge>
                                </h6>
                                <div v-if="settingsEditActive">
                                    <p class="mb-3 text-muted small">
                                        Select what data you want to collect from vendors for each line item.
                                    </p>
                                    <p class="mb-3 text-muted small">
                                        You can disable any of these on a per-line basis using the checkboxes in the grid below.
                                    </p>
                                </div>
                                <p v-else class="small text-muted">Select what data you want to collect from vendors for each line item.</p>
                            </div>
                            <div class="col">
                                <div v-if="settingsEditActive">
                                    <div class="">
                                        <div class="my-2 d-flex align-items-center">
                                            <b-form-checkbox
                                                v-model="quoteSheetData.vendorCols.enable_price"
                                                class="my-2"
                                            >Price</b-form-checkbox>
                                            <b-form-group
                                                id="fieldset-price-currency"
                                                label-cols="auto"
                                                label-size="sm"
                                                label="Base Currency"
                                                label-class="text-muted"
                                                class="my-0 ml-3"
                                            >
                                                <b-form-select 
                                                    v-model="quoteSheetData.currency" 
                                                    @input="handleBaseCurrencyInput"
                                                    :options="quoteSheetData.currency_choices" 
                                                    size="sm" 
                                                    class=""
                                                ></b-form-select>
                                            </b-form-group>
                                            <b-form-checkbox 
                                                v-if="!hasMultipleQuotedCurrencyOptions && enableQuotedCurrencyOptions"
                                                v-model="forceShowQuotedCurrencyOptions"
                                                switch 
                                                size="sm"
                                                class="ml-3"
                                            >Allow Supplier to Select Currency</b-form-checkbox >
                                        </div>
                                        <div v-if="showQuotedCurrencyOptions" class="pl-5">
                                            <b-form-group
                                                label="Allowed currencies for supplier quotes:"
                                                label-class="font-size-70 font-weight-500 text-muted text-uppercase"
                                                v-slot="{ ariaDescribedby }"
                                            >
                                                <b-form-checkbox
                                                    v-for="currOpt in quoteSheetData.currency_choices"
                                                    :key="currOpt.value"
                                                    v-model="quoteSheetData.quoted_currency_options_codes"
                                                    :value="currOpt.value"
                                                    :disabled="currOpt.value === quoteSheetData.currency"
                                                    :aria-describedby="ariaDescribedby"
                                                    name="quoted_currency_options_codes"
                                                    inline
                                                >{{ currOpt.text }}</b-form-checkbox>  <!-- must disable the base currency so it cannot be unselected -->
                                                <b-form-text>NOTE: When suppliers quote in a currency different from the Base Currency, their quoted prices are converted to the Base Currency at the market exchange rate at the time of quote.  The exchange rate will be shown to suppliers, and their original quoted prices in non-Base Currency and the exchange rate will be saved with their quote.</b-form-text>
                                            </b-form-group>
                                        </div>
                                    </div>
                                    <b-form-checkbox
                                        v-model="quoteSheetData.vendorCols.enable_subtotal"
                                        class="my-2"
                                    >Line Item Total<span class="small text-muted ml-2">Enabled lines will be included in Quote Total</span></b-form-checkbox>
                                    <b-form-checkbox
                                        v-model="quoteSheetData.vendorCols.enable_leadtime"
                                        class="my-2"
                                    >Lead Time</b-form-checkbox>
                                    <b-form-checkbox
                                        v-model="quoteSheetData.vendorCols.enable_moq"
                                        class="my-2"
                                    >MOQ<span class="small text-muted ml-2">Minimum Order Quantity</span></b-form-checkbox>
                                    <b-form-checkbox
                                        v-model="quoteSheetData.vendorCols.enable_deliverydate"
                                        class="my-2"
                                    >Delivery Date</b-form-checkbox>
                                    <b-form-checkbox
                                        v-model="quoteSheetData.vendorCols.enable_country_of_origin"
                                        class="my-2"
                                    >Country of Origin<span class="small text-muted ml-2">A drop-down list of countries will be shown to suppliers.</span></b-form-checkbox>
                                    <div class="my-2 w-100 d-flex align-items-center">
                                        <b-form-checkbox
                                            v-model="quoteSheetData.vendorCols.enable_shipterms"
                                        >Shipping Terms</b-form-checkbox>
                                        <b-form-group
                                            id="fieldset-shipterms-choices"
                                            label-cols="auto"
                                            label-size="sm"
                                            :disabled="!quoteSheetData.vendorCols.enable_shipterms"
                                            label="Choices"
                                            label-class="text-muted"
                                            class="my-0 ml-3 flex-fill"
                                        >
                                            <b-form-tags 
                                                input-id="input-shipterms-choices" 
                                                v-model="quoteSheetData.vendorCols.shipterms_choices"
                                                size="sm" 
                                                class="" 
                                                placeholder="Choices for Shipping Terms... (type something, hit enter)"
                                                :state="quoteSheetData.vendorCols.enable_shipterms ? (quoteSheetData.vendorCols.shipterms_choices.length ? null : false) : null"
                                            ></b-form-tags>
                                        </b-form-group>
                                    </div>
                                    <div class="my-2 w-100 d-flex align-items-center">
                                        <b-form-checkbox
                                            v-model="quoteSheetData.vendorCols.enable_payterms"
                                        >Payment Terms</b-form-checkbox>
                                        <b-form-group
                                            id="fieldset-payterms-choices"
                                            label-cols="auto"
                                            label-size="sm"
                                            :disabled="!quoteSheetData.vendorCols.enable_payterms"
                                            label="Choices"
                                            label-class="text-muted"
                                            class="my-0 ml-3 flex-fill"
                                        >
                                            <b-form-tags 
                                                input-id="input-payterms-choices" 
                                                v-model="quoteSheetData.vendorCols.payterms_choices"
                                                size="sm" 
                                                class="" 
                                                placeholder="Choices for Payment Terms... (type something, hit enter)"
                                                :state="quoteSheetData.vendorCols.enable_payterms ? (quoteSheetData.vendorCols.payterms_choices.length ? null : false) : null"
                                            ></b-form-tags>
                                        </b-form-group>
                                    </div>
                                    <b-form-checkbox
                                        v-model="quoteSheetData.vendorCols.enable_comment"
                                        class="my-2"
                                    >Comment<span class="small text-muted ml-2">Allow vendor to provide a text comment for each line-item</span></b-form-checkbox>
                                    <b-form-checkbox
                                        v-model="quoteSheetData.vendorCols.enable_nobid"
                                        class="my-2"
                                    >Allow No-Bid?<span class="small text-muted ml-2">Allow vendor to no-bid specific line items?</span></b-form-checkbox>
                                </div>
                                <div v-else class="d-flex flex-wrap my-1">
                                    <div v-for="vcol in vendorColsAll" :key="vcol.enableKey">
                                        <div 
                                            class="qsheet-col-chip" 
                                            :class="quoteSheetData.vendorCols[vcol.enableKey] ? 'qsheet-col-on' : 'qsheet-col-off'"
                                        >
                                            {{vcol.label}}
                                            <span v-if="!loading && vcol.enableKey === 'enable_price'" class="ml-1">
                                                ({{ quoteSheetData.currency }}{{ hasMultipleQuotedCurrencyOptions ? ' + multi' : '' }})
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

        </div>

        <div class="px-3 pb-3 flex-fill w-100 d-flex flex-column" :class="settingsEditActive ? 'opacity-50' : ''">  <!-- GRID -->

            <div class="flex-fill shadow" style="">
                <ag-grid-vue
                    ref="theGrid"
                    style="width: 100%; height: 100%;"
                    class="ag-theme-alpine-qs-builder"
                    :gridOptions="gridOptions"
                    :context="gridContext"
                    :defaultColDef="defaultColDef"
                    :columnTypes="columnTypes"
                    :columnDefs="columnDefs"
                    :rowData="rowData"
                    :defaultGroupSortComparator="defaultGroupSortComparator"
                    :rowClassRules="rowClassRules"
                    :loadingOverlayComponent="loadingOverlayComponent"
                    :loadingOverlayComponentParams="loadingOverlayComponentParams"
                    @grid-ready="onGridReady"
                    @row-drag-enter="onRowDragEnter"
                    @row-drag-move="onRowDragMove"
                    @row-drag-leave="onRowDragLeave"
                    @row-drag-end="onRowDragEnd"
                    @column-resized="updateRowHeights"
                    @grid-columns-changed="onGridColumnsChanged"
                    @cell-value-changed="onCellValueChanged"
                    @data-model-changed="onDataModelChanged"
                    @first-data-rendered="onFirstDataRendered"
                ></ag-grid-vue>
            </div>
            <!-- 
                @row-data-updated="setDirty" -- call this explicitly when needed instead
                :autoParamsRefresh="true"  Don't use this... just let entire components get destroyed/rebuilt 
                :defaultGroupSortComparator="defaultGroupSortComparator"
                @row-data-changed="setDirty"  Don't use this... you only use it after getting data from API, and it causes problems for timing of canSetDirty
            -->
        </div>  <!-- End Grid Area -->

        <div class="py-3 pl-3 bg-white" style="padding-right: 100px;">  <!-- Bottom Bar - big padding on right to avoid help widget button -->
            <div class="d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center">  <!-- Left Section bottom bar -->
                    <b-button 
                        :disabled="settingsEditActive || disableChanges"
                        variant="info" 
                        class="mr-3" 
                        @click="showItemPicker"
                    >
                        <b-icon icon="plus-circle"></b-icon>
                        Add Saved Items
                    </b-button>
                    <b-button 
                        :disabled="settingsEditActive || disableChanges"
                        variant="primary" 
                        class="mr-3" 
                        @click="addBlankRow(1)"
                    >
                        <b-icon icon="plus-circle"></b-icon>
                        Add Line Item
                    </b-button>
                    <b-button 
                        :disabled="settingsEditActive || disableChanges"
                        variant="light-primary" 
                        class="mr-3" 
                        @click="addBlankRow(20)"
                    >
                        <b-icon icon="plus-circle"></b-icon>
                        Add 20 Line Items
                    </b-button>
                    <div class="ml-4 font-size-80 font-weight-500">
                        <!-- Line Item area -->
                        <b-alert v-if="!loading && isTemplate" show variant="danger" class="mb-0">
                            This is a TEMPLATE - Line Items are not saved
                        </b-alert>
                        <div v-else class="font-size-80 font-weight-500">
                            <span class="mx-3">Total Items: <span class="font-weight-700">{{currentItemCount}}</span></span>
                            <span class="mx-3">Total Lines to Quote: <span class="font-weight-700">{{currentVariantCount}}</span></span>
                        </div>
                    </div>
                    
                </div>

                <div v-if="currentUserInfo.user_is_staff">
                    <b-dropdown size="sm" text="Admin Tools" variant="minimal-secondary">
                        <b-dropdown-item @click="gridApi.expandAll()">Expand</b-dropdown-item>
                        <b-dropdown-item @click="gridApi.collapseAll()">Collapse</b-dropdown-item>
                        <b-dropdown-item @click="dumpGrid">Dump Grid</b-dropdown-item>
                        <b-dropdown-item @click="dumpContext">Dump Context</b-dropdown-item>
                        <b-dropdown-item @click="prepareDataForApi">Dump API Data</b-dropdown-item>
                        <b-dropdown-item @click="validateAll">Validate</b-dropdown-item>
                        <b-dropdown-item @click="exportExcel">Excel</b-dropdown-item>
                        <b-dropdown-item @click="forceGridRefresh">forceGridRefresh</b-dropdown-item>
                        <b-dropdown-item @click="refreshRowModel">refreshRowModel</b-dropdown-item>
                        <b-dropdown-text>isEditAllowed: {{ isEditAllowed.toString() }}</b-dropdown-text>
                    </b-dropdown>
                </div>

                <div>  <!-- Middle Section bottom bar -->
                    <div v-if="hasSubmittedReplies" class="alert alert-danger">
                        <div 
                            v-b-popover.hover.top="'Quote sheets cannot normally be edited once vendors have submitted replies.'"
                            class="text-danger font-weight-bold font-size-80 mr-2"
                        >
                            <b-icon icon="exclamation-circle-fill" variant="danger" class="mr-1"></b-icon>
                            No Edits Allowed - Existing Replies or No Permission
                        </div>
                        <b-form-checkbox
                            v-if="allowNoEditOverrides && !readOnly"
                            :checked="overrideHasSubmittedReplies"
                            @input="handleOverrideHasSubmittedReplies"
                            switch
                            size="sm"
                        >Override & Allow Edits Anyways</b-form-checkbox>
                    </div>
                </div>
                
                <div class="d-flex justify-content-end align-items-center">  <!-- Right Section bottom bar -->
                    <div 
                        v-if="!isValidOverall" 
                        v-b-popover.hover.top="'Fix the highlighted errors.'"
                        class="text-danger font-weight-bold font-size-80 mr-2"
                    >
                        <b-icon icon="exclamation-circle-fill" variant="danger" class="mr-1"></b-icon>
                        Fix Errors
                    </div>
                    <div 
                        v-if="isDirty" 
                        v-b-popover.hover.top="'Quote Sheet has unsaved changes!'"
                        class="text-warning font-weight-bold font-size-80 mr-2"
                    >
                        <b-icon icon="exclamation-circle-fill" variant="warning" class="mr-1"></b-icon>
                        Unsaved Changes
                    </div>
                    <div>
                        <b-button
                            variant="secondary"
                            class="mx-2"
                            @click="requestClose"
                        >Close</b-button>
                        <b-button 
                            :disabled="settingsEditActive || disableChanges || !isValidOverall"
                            variant="success" 
                            @click="startSave"
                        >Save Quote Sheet</b-button>
                    </div>
                    
                </div>
            </div>
            
        </div>  <!-- End Bottom Bar -->

        <item-picker-modal
            ref="itemPickerModal"
            :existing-item-ids="linkedProductIds"
            :parent-loading="false"
            :allow-multiple-add="true"
            @add-item="addSavedItem"
        ></item-picker-modal>

    </div>
</template>

<script>
import { errorHandling } from '../../js/custom-vue-mixins';  // gives you apiErrorHandler(error)
import cloneDeep from 'lodash/cloneDeep';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import isNaN from 'lodash/isNaN';
import toNumber from 'lodash/toNumber';
//import sortedIndexBy from 'lodash/sortedIndexBy';
import { 
    QuoteSheet,
    LinkedProduct, 
} from './quote-sheet-common-helpers';
import { vendorColsAll } from './quote-sheet-vendor-cols';
import {
    QuoteSheetItem,
    QuoteSheetItemVariant,
    ActionButtonComponent,
    QuoteSheetItemFieldRenderer,
    QuoteSheetItemVueRenderer,
    makeBlankQsItem,
    makeBlankVariant,
} from './quote-sheet-builder-helpers';
import { AgGridVue } from 'ag-grid-vue';
import { 
    hasValueOrZero,
    toLuxonDateTime,
    toNumberOrNull,
} from '../../js/filters-helpers';
import { 
    QuoteLineCheckboxCellRenderer, 
    // MOVED ActionButtonComponent, 
    //QuoteSheetItemRenderer,
    QuoteSheetDescriptionRenderer, 
    SimplePlaceholderRenderer,
    SavingOverlayComponent,
    TitleSubtitleHeaderGroupRenderer,
    BootstrapVueDatePickerEditor,
    //VueDatePickerEditor,
    VueDateButtonRenderer,
} from './quote-sheet-components';
import ItemPickerModal from '../items/ItemPickerModal';
import { suppliosApi } from '../../js/api-service';
import { currentUserInfoMixin, tenantInfoMixin } from '../../js/custom-vue-mixins';
import {
    //agStringParser,
    //agStringFormatter,  // still relevant, maybe not needed though
    // agNumberParser,
    //agIsoDurationFormatter,
    agNumberFormatter,
    agNumberFixedDecimalParser,
    getEventPath,
    agDateFormatter,
} from '../../js/ag-grid-helpers';
import { parseDecimal } from '@/js/utils/numberUtils';
import { formatMoney } from '@/js/utils/moneyUtils';


// ag-grid helper methods - replaced with instance methods instead
// ---------------------------------------------------
// const variantGetter = function(params) {
//     // value getter for ag-grid cells to pull data from nested 'variant' object
//     let fieldKey = params.colDef.field;
//     //if (!params.data.variant[fieldKey]) return null;  // if we don't have any variants, return null
//     return params.data.variant[fieldKey];
// }

// const variantSetter = function(params) {
//     // value SETTER for ag-grid cells to put data into nested 'variant' object
//     // must return true on success so ag-grid knows to update the cell (false if not updated)
//     let fieldKey = params.colDef.field;  // figure out what field we're editing
//     if (!params.data.isVariant) return false;  // if this row isn't a variant, return false to discard the edit
//     params.data.variant[fieldKey] = params.newValue;  // params.data is the object/data for this row
//     return true;  // return true to let ag-grid know we updated it, so it refreshes
// }

// valueSetterParser -- our special parser to use inside valueSetter -- so we can validate at same time as parsing, and so its also use for PASTE operations
// these all must return { value, isValid }
//---------------------------------------------

const valueSetterParserString = (valueSetterParams) => {
    let newValue = valueSetterParams.newValue;
    let isValid = true;
    let value;
    const allowedEmptyValues = [null, '', ' ', undefined];  // adding ' ' single-space string to empty values, as this is common in the grid for empty strings -- we just coerce to our own empty value
    const emptyValue = '';  // the 'unset' value for this type/col
    if (allowedEmptyValues.includes(newValue)) {
        isValid = true;
        value = emptyValue;
    } else {
        // if its NOT one of our allowed empty values, just coerce to a string? 
        isValid = true;  
        value = String(newValue); 
    }
    return { value, isValid };
}

const valueSetterParserDate = (valueSetterParams) => {
    // custom validation/parser method used in valueSetter - return { value (any), isValid (boolean) }
    // handles values coming from editor and/or paste operations
    // we'll try parsing incoming dates as many ways as possible.  
    // MUST be able to pass in null/empty values to UNSET current value, or else it's never possible
    // Internal value is YYYY-MM-DD format as a string.
    console.log('Hello from valueSetterParserDate - incoming params:', valueSetterParams);
    let newValue = valueSetterParams.newValue;
    let isValid = true;
    let value;
    const allowedEmptyValues = [null, '', ' ', undefined];  // adding ' ' single-space string to empty values, as this is common in the grid for empty strings -- we just coerce to our own empty value
    const emptyValue = null;  // the 'unset' value for this type/col  should it be '' or null?
    if (allowedEmptyValues.includes(newValue)) {
        console.log('its an allowed empty value');
        isValid = true;
        value = emptyValue;
    } else {
        console.log('its NOT an allowed empty value');
        // check if we have an ISO duration string, OR if we can try parsing one
        // TODO - allow parsing from normal Excel formats (10/25/22) and JS Date() objects
        const maybeLuxonDateTime = toLuxonDateTime(newValue); //dtUtils.toLuxonDuration(valueSetterParams.newValue);
        const isValidDateTime = maybeLuxonDateTime && maybeLuxonDateTime.isValid;
        if (isValidDateTime) {
            console.log('it isValidDateTime');
            // if we could get a valid datetime from it
            isValid = true;
            value = maybeLuxonDateTime.toISODate();  // internal value is the ISO date string YYYY-MM-DD
        } else {  // NOT VALID
            console.log('its NOT isValidDateTime');
            value = emptyValue;
            isValid = false;
        }
    }
    return { value, isValid };
}

const valueSetterParserNumber = (valueSetterParams) => {
    // custom validation/parser method used in valueSetter - return { value (any), isValid (boolean) }
    // handles values coming from editor and/or paste operations
    // MUST be able to pass in null/empty values to UNSET current value, or else it's never possible
    let newValue = valueSetterParams.newValue;
    let isValid = true;
    let value;
    const allowedEmptyValues = [null, '', ' ', undefined];  // adding ' ' single-space string to empty values, as this is common in the grid for empty strings -- we just coerce to our own empty value
    const emptyValue = null;
    if (allowedEmptyValues.includes(newValue)) {
        isValid = true;
        value = emptyValue;
    } else if (isNaN(toNumber(newValue))) {
        // if its NOT one of our allowed empty values, and cannot be coerced to a number (returns NaN) then we IGNORE IT
        isValid = false;  // ignore it - don't set it
        value = null;  // doesn't matter actually
    } else {
        value = toNumberOrNull(newValue);
        isValid = true;
    }
    return { value, isValid };
}

// on to our regularly scheduled programming....
// ---------------------------------------------------------------

export default {
    name: 'QuoteSheetBuilder',
    components: {
        AgGridVue,
        ItemPickerModal,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,  // adds computed property for $currentUserInfo/currentUserInfo
        tenantInfoMixin,  // adds $tenantInfo computed
    ],
    props: {
        quoteSheetId: {
            type: [String, Number],
            required: true, 
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            // ag-grid stuff
            gridOptions: null, // big config object for ag-grid
            gridContext: null,  // (context) set in beforeMount, separate from gridOptions, then update if/when needed
            defaultColDef: null,  // set all this stuff in some hook like beforeMount
            columnTypes: null,
            gridApi: null,  // set this in mounted, easy way to manipulate grid on the fly
            gridColumnApi: null,  // set this in mounted, easy way to manipulate grid on the fly
            defaultGroupSortComparator: null,  // used to set ORDERING of groups - set in beforeMount
            columnDefs: null,
            rowData: [], // set these in beforeMount() or somewhere like that --- DO NOT USE V-MODEL -- it kinda works, but causes funky stuff when updating the grid (like collapsing all groups, etc.) - let grid manage its own data
            rowClassRules: null,  
            loadingOverlayComponent: 'savingOverlayComponent',
            loadingOverlayComponentParams: {loadingMessage: 'Loading...'},  // overwrite entire objectin a method... better than doing a computed property because it will happen right away
            // FLAGS
            loading: false,  // initial load of data
            saving: false,  // loading indicator when saving data
            gridBusy: false,  // set this to true when grid is processing/updating stuff and you want to disable things like save button
            // you combine loading, saving, gridBusy in one computed property
            // ROW DRAG STUFF
            parentItemDragClass: 'parent-item-drag',  // (constant) class that gets applied to the column where we trigger drags for the entire group / parent QuoteSheetItem (not variants) - we specifically look for this class in our drag event function too
            isParentDrag: false,  // toggle for CURRENT drag event, set when starting drag, reset when done with drag
            highlightDropNode: null,  // the node to HIGHLIGHT a border of -- use with above/below 
            highlightDropNodeAbove: true,  // boolean to use with highlightDropNode -- if true, highlight TOP BORDER, if false, highlight BOTTOM BORDER
            // VENDOR COLS
            vendorColsAll: vendorColsAll,  // set this.vendorColsAll from imported vendorColsAll
            settingsEditActive: false,  // toggle for editing column visibility & other metadata in settings panel
            quoteSheetDataBeforeEdit: null,  // we clone quoteSheetData when we go into settings/columns edit mode and stash here
            enabledVendorColKeysBeforeEdit: new Set(),  // SET of vendor cols that are enabled before we start editing... only set when we enter settingsEditActive
            // MY DATA
            quoteSheetData: new QuoteSheet({}),  // instantiate empty one as placeholder until API stuff loads -  TOP-LEVEL FIELDS FOR QuoteSheet object, nested vendorCols and internalCols - put v-models to this
            hasSubmittedReplies: false,  // get from API on initial load, then use as input to lock editing
            overrideHasSubmittedReplies: false,  // put this v-model to a switch, if true, then still allow edits, overriding hasSubmittedReplies
            allowNoEditOverrides: false,  // must enable this to show the overrideHasSubmittedReplies checkbox -- FOR YOUR USE ONLY
            isEditAllowed: false, // IMPORTANT, with a method to update it, instead of just a method or computed
            forceShowQuotedCurrencyOptions: false,  // you use this in a computed to control whether the quoted_currency_options_codes picker is showing or not
            enableQuotedCurrencyOptions: true,  // TOGGLE FOR HIDING IT WHILE ROLLING OUT THIS FEATURE ONLY
            timeframeOptions: ['', '-', 'per hour', 'per day', 'per week', 'per month', 'per year', 'per order', 'total'],
            currentItemCount: null,  // set when grid data is updated
            currentVariantCount: null,  // set when grid data is updated
            linkedProductIds: [],  // array of product/item IDs for linked_products... used as prop for item picker -- update when grid data is updated inside update method
            invalidNodeIdMap: new Map(),  // Map of node IDs (keys) and array of invalid fields (values) that are NOT VALID -- use with rowClassRules or cellClassRules to highlight invalid things, then clear/refresh when changes are made
            // NOTE:  invalidNodeIdMap will not be reactive unless you use Vue.set somehow...
            hasInvalidRows: false,  // any invalid ROWS?  UPDATE THIS FROM A METHOD -- because you were having problem with reactivity on computed property for invalidNodeIdMap.size
            choicesInvalid: false, // if one of our choices cols is invalid, set this to true
            hasVariants: true,  // Boolean, START WITH TRUE - true if we have any items with multiple variants - used to show/hide variant col?  update it from this.updateCounts()
            canSetDirty: false, // used to ignore setting isDirty true until first load is done (then set to true)
            isDirty: false,  // Boolean, set to TRUE in various places where you change things
            // NEW - Aug 9 2022 - allow for item 'name' to be optional, use part_number instead (one is required)
            usePnForName: false,  // same attr name as QuoteSheetItem JS class - when this is updated, you need to call setUsePnForName(true/false) on each QuoteSheetItem
            // change this explicitly
        }
    },
    computed: {
        disableChanges: function() {
            let blockEdits = !this.isEditAllowed;  // centralize all logic for 'blocking edits' here based on replies, etc.
            return this.loading || this.saving || this.gridBusy || blockEdits;  
        },
        isValidOverall: function() {
            // combines multiple things from data to report overall validity 
            return !this.hasInvalidRows && !this.choicesInvalid;
        },
        blankRowCount() {
            // number of blank rows to add by default for a NEW QuoteSheet
            // pulls from $tenantInfo.custom_flags.quotesheet_defaults.blank_rows if it has it... 
            // otherwise defaults to 0?  1?  MUST ALWAYS RETURN INTEGER
            return this.$tenantInfo.custom_flags.quotesheet_defaults?.blank_rows ?? 0;
        },
        missingNameAndPartNumber() {
            // do we have show_name and show_part_number BOTH turned off?  cannot...
            return !this.quoteSheetData?.internalCols?.show_name && !this.quoteSheetData?.internalCols?.show_part_number;
        },
        isTemplate() {
            // is this a TEMPLATE QuoteSheet?
            return this.quoteSheetData?.is_template;
        },
        hasMultipleQuotedCurrencyOptions() {
            return this.quoteSheetData.quoted_currency_options_codes?.length && this.quoteSheetData.quoted_currency_options_codes.length > 1;
        },
        showQuotedCurrencyOptions() {
            // show the quoted_currency_options_codes picker?  if you have multiple allowed currencies, or if user clicks button to forceShow, then show it
            return this.forceShowQuotedCurrencyOptions || this.hasMultipleQuotedCurrencyOptions;
        },
    },
    watch: {
    },
    created: function() {
    },
    beforeMount: function() {
        // ag-grid setup
        this.gridOptions = {
            // moved context to separate object with direct binding to grid
            components: {
                simplePlaceholderRenderer: SimplePlaceholderRenderer,  // there is a Vue version with same name... don't use both
                quoteSheetItemFieldRenderer: QuoteSheetItemFieldRenderer,
            },
            frameworkComponents: {
                // use componentName: ComponentName format (note small letter, big letter)
                // then use: colDef: {cellRenderer: 'componentName'}  // small letter, string name
                quoteLineCheckboxCellRenderer: QuoteLineCheckboxCellRenderer,
                //quoteLineTreeColRenderer: QuoteLineTreeColRenderer,
                actionButtonComponent: ActionButtonComponent,
                quoteSheetItemVueRenderer: QuoteSheetItemVueRenderer,
                quoteSheetDescriptionRenderer: QuoteSheetDescriptionRenderer,
                savingOverlayComponent: SavingOverlayComponent,
                titleSubtitleHeaderGroupRenderer: TitleSubtitleHeaderGroupRenderer,
                bootstrapVueDatePickerEditor: BootstrapVueDatePickerEditor,
                //vueDatePickerEditor: VueDatePickerEditor,
                vueDateButtonRenderer: VueDateButtonRenderer,
            },
            tooltipShowDelay: 500,  // in ms
            enterMovesDown: true,
            enterMovesDownAfterEdit: true,
            //rowSelection: "multiple",
            enableRangeSelection: true,
            enableFillHandle: true,
            fillHandleDirection: 'y',
            /* fillOperation: (params) => {  // params is big here... can return things differently based on cell/row/col
                // but can't seem to just turn off filling... ?
                console.log('fillOperation params');
                console.log(params);
                if (params.column.colId === 'ag-Grid-AutoColumn') {
                    return false;  // returning false does not disable filling... it just makes it fill NORMALLY
                }
            }, */
            undoRedoCellEditing: true,  // seems to not work on tree data since it does auto-grouping
            //rowDragManaged: true,  // default is false
            animateRows: true,  // false is default, better performance
            //suppressRowTransform: true,  // needed for row spanning, but hurts performance
            groupHeaderHeight: 70,
            headerHeight: 50,
            //rowHeight: 32,  // setting this, even though we have autoHeight on - or set in grid css settings instead?
            // ROW GROUPING - you will actually group based on a hidden column that has the item ID, defined in colDefs
            groupSuppressAutoColumn: true,  // suppress it so we can better control it ourselves
            groupHideOpenParents: true,
            groupDefaultExpanded: -1,  // number of levels to expand by default... -1 means all, always
            //groupSelectsChildren: true,
            //groupMultiAutoColumn: true,
            getRowNodeId: function(data) {
                return data.guid;  // should return strings -- the guid of each variant
            },  // use this to have grid use your own defined node IDs... or let it use its own.  should return strings.
            // makes it easier to get/call row nodes later, such as:  var rowNode = api.getRowNode('55');
        };
        this.defaultGroupSortComparator = (nodeA, nodeB) => {  // IMPORTANT - used to set sort order for GROUPS in the grid
            // return -1, 0, or 1 like standard array sort 
            let nodeA_so = nodeA.key ? this.getQsItem(nodeA.key).sort_order : 0;
            let nodeB_so = nodeB.key ? this.getQsItem(nodeB.key).sort_order : 0;
            /* console.log('hello from comparator');
            console.log(nodeA);
            console.log(nodeB); */
            if (nodeA_so < nodeB_so) {
                return -1;
            } else if (nodeA_so > nodeB_so) {
                return 1;
            } else {
                return 0;
            }
        };
        this.gridContext = {  // we will add more later after loading data from API
            componentParent: this,  // then child 'framework components' inside here can access this 'parent' component via this.params.context.componentParent
            currency: this.quoteSheetData.currency,  // this sets initial value, but its not reactive... have to update explicitly on changes
            qsItemMap: new Map(),  // instantiate this, fill it up when you get data from API, and when adding things to grid
            linkedProductMap: new Map(),  // instantiate this, fill it up when you get data from API, and when adding things with linked products
        };
        this.rowClassRules = {
            // REPLACING 'border-dnd'
            'border-dnd-top': (params) => {
                let isThisNode = this.highlightDropNode && this.highlightDropNode.id === params.node.id;
                return isThisNode && this.highlightDropNodeAbove;
            },
            'border-dnd-bottom': (params) => {
                let isThisNode = this.highlightDropNode && this.highlightDropNode.id === params.node.id;
                return isThisNode && !this.highlightDropNodeAbove;
            },
            'row-invalid': (params) => {
                // do not use this.invalidNodeIdMap here... that gets updated AFTER the edit is done, so there will be a delay in updating rowClass
                let nodeIsValid = this.nodeIsValid(params.node);
                //console.log(`From rowClassRules row-invalid - nodeIsValid? ${nodeIsValid}`);
                return !nodeIsValid;
            },
            /*'row-invalid': (params) => {  // if this rowData is invalid, apply this... AND at least one of the classes below
                return params.data.errorKeys.length > 0;
            },
            'row-invalid-item-details': (params) => {  // BaseQuoteSheetGridRow.errorKeys is an array of error types for each row
                return params.data.errorKeys.includes('itemDetails');
            },
            'row-invalid-variant-data': (params) => {  // BaseQuoteSheetGridRow.errorKeys is an array of error types for each row
                return params.data.errorKeys.includes('variantData');
            },
            'row-invalid-grid-system': (params) => {  // BaseQuoteSheetGridRow.errorKeys is an array of error types for each row
                return params.data.errorKeys.includes('gridSystem');
            }, */
            'item-multiple-variants': this.hasMultipleVariants,
            'item-onlyrow': this.hasOneVariant,
            'item-firstrow': this.isFirstVariant,
            'item-midrow': (params) => {return !this.isFirstVariant(params) && !this.isLastVariant(params)},  // not first and not last
            'item-lastrow': this.isLastVariant,
            'item-has-linked-product': this.hasLinkedProduct,
            //'row-last-of-group': this.isLastVariant,
        };
        this.defaultColDef = {
            resizable: true,
            suppressMenu: true,  // hides the menu for each col... set false if you want filters, or use inline/floating? filters
            //wrapText: true, // use this together with autoHeight -- only add to individual cols since autoHeight is so expensive
            //autoHeight: true,  // use sparingly... very expensive for rendering -- put on individual cols instead
            singleClickEdit: false,
            enableCellChangeFlash: true,
        };
        this.columnTypes = {
            // 'variantData': {
            //     valueGetter: this.qsVariantGetter,  // variantGetter,
            //     valueSetter: this.qsVariantSetter, // variantSetter,
            //     cellClassRules: {
            //         'bg-gray-150': this.isParentRow,
            //     },
            // },
            'quantityCol': {
                editable: () => this.isEditAllowed,
                // cellClass: ['bg-warning-50'],
            },
            'internalPrice': {  // TODO - these are not used, need updating if/when used - look at other cols for reference
                editable: () => this.isEditAllowed,
                cellClass: ['bg-success-50'],
                minWidth: 70,
                maxWidth: 120,
                flex: 1,
                // valueParser: agNumberParser,  // TODO - this may not be right...
                // valueFormatter: (params) => {
                //     return FormatCurrencyHelper(params.value, params.context.currency);
                // },
            },
            'firstChildVisible': {  // use in cols where you only want first child to be visible... use with groupHideOpenParent row grouping
                cellClassRules: {
                    'invisible': (params) => {return !this.isFirstVariant(params)},
                },
            },
        };
        this.columnDefs = this.buildColDefs();  // this will return colDefs as defined, but some vue data may not be ready yet... so might have to call/update later?
    },
    mounted: function() {
        // ag-grid stuff
        this.gridApi = this.gridOptions.api;
        this.gridColumnApi = this.gridOptions.columnApi;     
        this.getData();  // should you move earlier in lifecycle???
    },
    methods: {
        handleBaseCurrencyInput(currencyCode) {
            // called from @input of currency (base currency) selector
            // we need to also make sure this currency is in quoted_currency_options_codes
            // then it will be disabled in there, so it cannot be deselected
            if (!this.quoteSheetData.quoted_currency_options_codes.includes(currencyCode)) {
                this.quoteSheetData.quoted_currency_options_codes.push(currencyCode);
            }
        },
        requestClose: function() {
            if (this.isDirty) {
                this.$bvModal.msgBoxConfirm('Are you sure you want to close without saving changes?', {
                        title: 'Unsaved Changes',
                        size: 'sm',
                        buttonSize: 'sm',
                        okVariant: 'danger',
                        okTitle: 'Yes, Close',
                        cancelTitle: 'No, Cancel',
                        footerClass: 'p-2 border-0',
                        hideHeaderClose: false,
                        centered: true
                    })
                    .then(clickedOk => {
                        if (clickedOk) {
                            this.$emit('request-close');  // tells parent to close the modal
                        }
                    })
            } else {  // if not isDirty, then just emit close event right away
                // emit event to parent to close quoteSheet - or do it if this has the modal
                this.$emit('request-close');
            }
        },
        resetValidation: function() {
            // reset invalidNodeIdMap and other things
            this.invalidNodeIdMap = new Map();  // don't use .clear... it won't be reactive
            this.updateHasInvalidRows();
            this.redrawAllRows();
        },
        updateHasInvalidRows: function() {
            // CALL THIS TO UPDATE data.hasInvalidRows boolean -- use this instead of computed property since you had reactivity problems
            // note on July 25 2022 -- you probably had 'reactivity problems' because Maps are not reactive... you should change how you do this
            // either use object (with vue methods for updating that object in reactive way... vue.set, etc.) or something else
            this.hasInvalidRows = this.invalidNodeIdMap.size > 0;
        },
        validateAll: function() {  
            // MUST RETURN BOOLEAN OF THINGS VALID OR NOT -- we call this in save method
            // NO REFRESH / REDRAW IN HERE
            // AUG 6 2022 - handle EMPTY nodes separately
            let emptyNodes = [];
            let nonEmptyNodes = [];
            let invalidNodes = [];
            // make arrays of things that must be unique, then check at the end
            let variantGuidList = [];
            let variantIdList = [];  // only put things here if they have an ID
            // run through all nodes, validate each individually, add to list of things for uniqueness checks
            this.gridApi.forEachNode((node, index) => {
                // MUST CHECK IF WE HAVE DATA -- because this will loop through all 'dummy' group nodes too
                //console.log('from validateAll, checking this node:');
                //console.log(node);
                if (node.group) {
                    //console.log('skipped -- dummy group node');
                    return;
                }
                if (this.isRowEmpty({rowNode: node})) {
                    // if its empty, just skip the rest of the loop, we'll discard this row anyways
                    emptyNodes.push(node);
                    return;
                } else {
                    nonEmptyNodes.push(node);
                }
                // now continue checking the real NON-EMPTY variant nodes -- add things to unique lists, then check isValid()
                variantGuidList.push(node.data.guid);
                if (node.data.id) {
                    variantIdList.push(node.data.id);
                } 
                //let nodeIsValid = node.data.isValid();
                let nodeIsValid = this.validateSingleNode(node);
                //console.log(nodeIsValid);
                if (!nodeIsValid) {
                    console.log(`Found INVALID NODE at index ${index}`);
                    invalidNodes.push(node);
                }
            });
            if (invalidNodes.length) {
                console.log('All Invalid Nodes:');
                console.log(invalidNodes);
            } else {
                console.log('ALL INDIVIDUAL NODES VALID');
            }
            // ALREADY DOING THIS IN VALIDATESINGLENODE 
            let individualNodesValid = this.invalidNodeIdMap.size === 0 ? true : false;  // get rid of this??? seems to duplicate what we just did above...
            let allUniqueOk = this.checkUnique(variantGuidList) && this.checkUnique(variantIdList);
            let uniqueValidMsg = allUniqueOk ? 'All variant uniqueness checks OK' : 'Problem with uniqueness checks on variants!!!';
            console.log(uniqueValidMsg);
            // OLD - validated all qsItems, even if empty
            //let qsItemsValid = this.validateQsItems();
            // NEW - only do it for non-empty rows - have to check if entire row is empty, not just qsItem or variant separately... must both be empty for a row to be ignored
            let qsItemsValid = this.validateQsItems(nonEmptyNodes);
            let choicesColsValid = this.validateChoicesCols();
            console.log(`Choices cols OK? ${choicesColsValid}`);
            let everythingValid = individualNodesValid && allUniqueOk && qsItemsValid && choicesColsValid;
            console.log(everythingValid ? 'EVERYTHING VALID' : 'VALIDATION ERROR!!!');
            this.updateHasInvalidRows();
            return everythingValid;
        },
        checkUnique: function(someArray) {
            // generic util for checking if all values in someArray are unique -- make a unique version using lodash uniq(), compare length
            return someArray.length === uniq(someArray).length;
        },
        validateChoicesCols: function() {
            // make sure any enabled columns that need choices actually have choices
            let colsWithChoices = ['payterms', 'shipterms'];  // make sure these match the actual things for 'enable_' and '_choices'
            let choicesValid = true;  // default... then set false if you find problem
            colsWithChoices.forEach(choiceCol => {
                let enableColName = 'enable_' + choiceCol;
                let isEnabled = this.quoteSheetData.vendorCols ? this.quoteSheetData.vendorCols[enableColName] : false;
                if (isEnabled) {
                    let choicesName = choiceCol + '_choices';
                    let choicesArray = this.quoteSheetData.vendorCols[choicesName];  // should be an array
                    let choicesOk = choicesArray ? !!choicesArray.length : false;
                    if (!choicesOk) {choicesValid = false}  // never set it true, just set false when starting from true
                }  // if NOT enabled, then don't set to false
            })
            this.choicesInvalid = !choicesValid;
            return choicesValid;
        },
        validateQsItems: function(rowNodes) {
            // perform validation on an array of QuoteSheetItem instances in gridContext
            // must either pass in an array of rowNodes to check, OR it will get all by default
            // validate each individually, make sure sort_order, guid, id (if any) are all unique 
            // NO REFRESH / REDRAW IN HERE
            // NEW Aug 6 2022 - check if its empty first... 
            //                  if empty and all variants ALSO empty, then ignore it - we won't save it
            //                  if empty and HAS NON-EMPTY variants, that's a problem  (another reason you need item and variant in the same object... makes this much easier)
            const allQsItems = this.getAllQsItems();
            let qsItems;  // the ones we want to check
            if (rowNodes) {
                const qsItemGuids = rowNodes.map(n => n.data?.qs_item_guid).filter(guid => !!guid);
                qsItems = allQsItems.filter(qsi => qsItemGuids.includes(qsi.guid));
            } else {
                qsItems = allQsItems;
            }
            let sortOrderList = [];
            let guidList = [];
            let idList = [];
            let invalidQsItems = [];
            qsItems.forEach(qsItem => {
                sortOrderList.push(qsItem.sort_order);
                guidList.push(qsItem.guid);
                if (qsItem.id) idList.push(qsItem.id);
                if(qsItem.isValid() === false) {
                    invalidQsItems.push(qsItem);
                    console.log(`Found invalid qsItem: sort_order = ${qsItem.sort_order} | guid = ${qsItem.guid}`);
                }
            });
            // now check uniqeness of all unique item lists
            let individualItemsValid = invalidQsItems.length === 0;
            let allUniqueOk = this.checkUnique(sortOrderList) && this.checkUnique(guidList) && this.checkUnique(idList);
            //console.log(individualItemsValid ? 'All QsItems valid individually' : 'Some QsItem(s) invalid!!!');
            //console.log(allUniqueOk ? 'All QsItem uniqueness checks OK' : 'Problem with uniqueness checks on QsItems!!!');
            return individualItemsValid && allUniqueOk;
        },
        getInvalidFields: function(rowNode) {
            // return an ARRAY of col names / field names for which cols are NOT VALID for this rowNode
            // uses methods on QuoteSheetItem and QuoteSheetItemVariant, then checks if parent item has multiple variants, and make sure we have variant description if multiple
            // Aug 6 2022 - first we'll check if the entire row is empty... if so, we return empty array, so no invalid fields
            if (this.isRowEmpty({rowNode: rowNode})) {
                return [];
            }
            let variantInvalidFields = rowNode.data.getInvalidFields();  // returns an ARRAY
            let qsItem = rowNode.data.qs_item_guid ? this.getQsItem(rowNode.data.qs_item_guid) : null;
            let qsItemInvalid = qsItem ? qsItem.getInvalidFields() : [];  // should always be an ARRAY
            let qsItemInvalidFields = qsItemInvalid.map(ifld => `item_${ifld}`);  // prepend all qsItem fields with item_
            let invalidFields = [...variantInvalidFields, ...qsItemInvalidFields];
            if (rowNode.parent.allChildrenCount > 1 && !rowNode.data.description) {
                invalidFields.push('description');
            }
            return invalidFields;
        },
        validateRowNodes: function(rowNodes) {
            // validate an ARRAY of rowNodes -- runs validateSingleNode on each one
            rowNodes.forEach(rn => this.validateSingleNode(rn));
        },
        nodeIsValid: function(rowNode) {
            // the actual validation check for a single rowNode, called from rowClassRules function, so should only get things from args passed in there
            // call this from other places, but don't use this to update other things... keep this super scoped down
            // RETURN Boolean for whether this rowNode is valid or not
            // THIS WILL GET CALLED EVEN FOR DUMMY GROUP NODES -- so deal with those -- they will have rowNode.group = true
            // UPDATE Aug 6 2022 - IF NODE IS COMPLETELY EMPTY (a real data row, but everything is TOTALLY empty), we DO NOT CALL THAT INVALID - we'll ignore it when prepping for save to API
            //                      so return true for those empty rowNodes
            if (rowNode.group) {
                return true;  // just pass it through if a dummy group node
            }
            if (this.isRowEmpty({rowNode})) {
                return true;
            }
            // if NOT a group rowNode (ie: a real one), NOT empty, then check validation
            let variantValid = rowNode.data.isValid();
            // TODO? maybe get qsItem from params.context instead?
            let qsItem = rowNode.data.qs_item_guid ? this.getQsItem(rowNode.data.qs_item_guid) : null;  // make sure it HAS a qs_item_guid, then get the qsItem
            let qsItemValid = qsItem ? qsItem.isValid() : false;
            let variantDescriptionOk = (rowNode.parent.allChildrenCount > 1) ? !!rowNode.data.description : true;  // if parent item has multiple variants, do we have variant description?
            // NOTE - these are duplicate checks in getInvalidFields -- make sure these are consistent!
            return variantValid && qsItemValid && variantDescriptionOk; 
        
        },
        isRowEmpty({rowNode, rowData}) {
            // can take EITHER 'rowNode' or 'rowData' as param - to allow for more flexible use from other methods
            // return boolean - true if this node is TOTALLY empty of entered data (untouched row) -- we'll discard these before saving to API
            // we might get group nodes in here, which don't even have .data
            //      return null for those - handle differently
            // we must check both the variant AND the qs_item separately... 
            //      because we don't have both of them in the rowData for now... (but you should)
            // for each one, just call method on the respective class instance
            // if variant is empty and its the FIRST variant, then must check qsItem also
            // if variant is empty and it is NOT the first variant, we consider it empty
            const dataObj = rowNode ? rowNode.data : rowData;
            // that might be undefined if we passed in a group rowNode
            if (!dataObj) {
                return null;
            }
            const variantEmpty = dataObj.isEmpty();
            if (!variantEmpty) {
                return false;
            }
            // if variant IS empty, we need to check if its the first variant and linked qs_item
            // ACTUALLY, this works, but I don't like the UX... because it could remove the 2nd variant, leaving only one variant, an wipe away the variant description unintentionally
            //      so, for now, let's say the auto-removal of extra rows does not work on extra variant rows... ony if the qsItem is also blank
            //      commenting out part below as a result
            /* 
            // get the rowNode also, need it below
            // if you passed in rowData instead of node, get the node using 'id' (from dataObj.guid)
            const node = rowNode ?? this.gridApi.getRowNode(dataObj.guid);
            const isFirstVariant = !!node.firstChild;  // node.parent.allChildrenCount === 1;
            if (!isFirstVariant) {
                return true;  // if variant is empty but its NOT the first variant, then we can consider it empty
            } */
            // if it IS first (or only) variant, now we must check the qsItem also 
            const qsItem = dataObj.qs_item_guid ? this.getQsItem(dataObj.qs_item_guid) : null;
            if (!qsItem) {
                return null;  // should not happen since we checked for group nodes above...
            }
            return qsItem.isEmpty();  // we only get here if variant is also empty
        },
        validateSingleNode: function(rowNode) {
            // validate all things for a single rowNode, WITHOUT ANY REFRESH/REDRAW -- just a utility to use elsewhere
            // must validate these things:
            //  - QuoteSheetItemVariant
            //  - QuoteSheetItem (its FK)
            //  - LinkedProduct?  if any
            //  - if its a variant (parent item has multiple variants) then must have description
            // add this to this.invalidNodeIdMap if invalid AND return Boolean of validity
            //console.log('from validateSingleNode');
            //console.log(rowNode);
            // THIS WILL GET CALLED EVEN FOR DUMMY GROUP NODES -- so deal with those -- they will have rowNode.group = true
            let nodeIsValid = this.nodeIsValid(rowNode);
            //console.log(`Found INVALID NODE at index ${rowNode.childIndex}`);
            // add/remove from invalid node id set
            if (nodeIsValid) {
                // remove it from invalid Map, in case it was in there -- but must do it in special way to keep it reactive
                let newMap = new Map();
                this.invalidNodeIdMap.forEach((value, key) => {
                    if (key !== rowNode.id) {
                        newMap.set(key, value);
                    }
                });
                this.invalidNodeIdMap = newMap;  
            }  // remove it from invalid Map, in case it was in there
            if (!nodeIsValid) {
                let invalidFieldArray = this.getInvalidFields(rowNode);  // should always return an array
                //this.invalidNodeIdMap.set(rowNode.id, invalidFieldArray);  // if invalid, add it to our invalid Map 
                let newInvalidMap = this.invalidNodeIdMap.set(rowNode.id, invalidFieldArray);  // if invalid, add it to our invalid Map -- Map.set() returns the Map, which we must set as the new top level data property to keep it reactive
                this.invalidNodeIdMap = newInvalidMap;
                console.log(`INVALID NODE! rowNode ID ${rowNode.id}`);
                console.log(invalidFieldArray);
            }  
            this.updateHasInvalidRows();
            return nodeIsValid;
        },
        /* getQuoteSheetData: function() {
            // DRY way to get snapshot of current quoteSheetData -- returns a CLONE of the data
            return cloneDeep(this.quoteSheetData);
        }, */
        addBlankRow: function(qty) { // add a blank Row to the grid
            // make a blank QsItem --> add to context, then make a blank Variant --> add row to grid
            // NEW Aug 9 2022 - allow adding MULTIPLE - so we can add in bulk on new quote sheets
            const rowCount = qty || 1;
            if (rowCount < 1) {
                return;  // just bail out if we pass in 0 -- which CAN happen if we call this when adding initial blank rows, but set to 0
            }
            const defaultCurrency = this.quoteSheetData?.currency || '';
            let sortOrder = this.gridContext.qsItemMap.size;  // PREVIOUS sort order - set to number of items in context now... we'll increment each time we add one
            const newRows = [];
            for (let count = 0; count < rowCount; count++) {
                sortOrder = sortOrder + 1;  // increment first... sort order for NEW item 
                let newQsItem = makeBlankQsItem({quotesheet_id: this.quoteSheetId, sort_order: sortOrder, use_pn_for_name: this.usePnForName});
                this.updateQsItemSortOrder({
                    newQsItemObj: newQsItem, 
                    newSortOrder: sortOrder, 
                    skipRefresh: true,  
                });  // this does the insert into context, and reindexes all sort_orders.  skip refresh, and call it at end of this function if needed
                // now make new variant -- this is the rowData - the new row 
                let newVariantObj = makeBlankVariant({quotesheet_item: newQsItem, sort_order: 1, default_currency: defaultCurrency});
                newRows.push(newVariantObj);
            }
            // we now have 1 or more new rowData objects to add -- will use our bulk method to add to grid in transaction
            this.addRowsToGrid({newDataRows: newRows});
            // Maybe don't call refresh / redraw?  So we don't immediately highlight invalid node?  No... we have to change rowClasses for cell renderers...
            // no setDirty here... not for blanks
        },
        addRowsToGrid: function({newDataRows}) {
            console.log(`FROM addRowsToGrid - is grid ready? ${this.$refs.theGrid?.gridReadyFired}`);
            // add a VARIANT ROW to the grid -- uses applyTransaction, 
            // variant data should have 'qs_item_guid' for a QuoteSheetItem that is already in the context
            let addResult = this.gridApi.applyTransaction({
                add: newDataRows,
            });
            let firstNode = addResult.add[0];
            // refresh cells / redraw rows -- we'll redraw this row and all siblings to make sure all rowClasses are updated
            // must also run validation first
            this.redrawGroup(firstNode);
            //let allSiblingNodes = firstNode.parent.childrenAfterSort;
            //this.gridApi.redrawRows({rowNodes: allSiblingNodes});
            // set focused cell to item column of first added row
            this.setFocusedCell({rowNode: firstNode, colKey: 'item_name'});  // TODO - change colKey based on whether we have variant or top level item?
            // DO NOT CALL setDirty here -- not always needed - call in caller if needed
        },
        setFocusedCell({rowIndex, rowNode, rowNodeId, colKey}) {
            // scroll to this node/index & set focus on a certain cell in a certain row/index
            // can provide rowIndex OR rowNode OR rowNodeId (priority in that order)
            // just a wrapper around this.gridApi.setFocusedCell() with args that make more sense for you / defaults
            let finalColKey = colKey || 'item_name';
            let finalRowIndex = null;
            if (rowIndex) {
                finalRowIndex = rowIndex;
            } else if (rowNode) {
                finalRowIndex = rowNode.rowIndex;
            } else if (rowNodeId) {
                finalRowIndex = this.gridApi.getRowNode(rowNodeId).rowIndex;
            }
            if (finalRowIndex) {
                this.gridApi.ensureIndexVisible(finalRowIndex);  // optional 2nd arg top/middle/bottom, if omitted it does minimal scroll possible
            }
            if (finalRowIndex && finalColKey) {
                this.gridApi.setFocusedCell(finalRowIndex, finalColKey, null);
            }
        },
        cloneNode: function(rowNode, placeAbove, cloneGroup) {  // NEW version
            // clone an existing rowNode from the grid -- must pass in a rowNode (ag-grid object, our row data is inside that)
            // NOTE: rowNode here will always be a variant row, so if cloning group, need to get parent first
            // placeAbove (Boolean) - if true, we place it at index ABOVE the node passed in.  default is false, so we place below this node
            // cloneGroup (Boolean) - if true, clone the entire group, if false, just clone the variant and put it in same group
            // this should dynamically handle cloning an entire group (QuoteSheetItem + its variants) or just a single variant, but rowNode should always be a variant
            // let's make sure rowNode is NOT a group node (ie: one of the hidden dummy 'group' rowNodes)
            if (rowNode.group) {
                console.error('From cloneNode, this only handles cloning Variant rowNodes, not dummy group rowNodes');
                return;  // just return now if we find this problem
            }
            //let affectedRowNodes = [rowNode];  // add things here as you go, will redrawRows on these at the end
            if (!cloneGroup) {  // just clone the VARIANT
                let assignedSortOrder = placeAbove ? rowNode.data.sort_order : rowNode.data.sort_order + 1;  // the sort_order for the cloned row
                let clonedVariantData = rowNode.data.clone({sort_order: null});  // use the .clone() method on QuoteSheetItemVariant instance -- and set sort_order to null for now, it will keep the same 'qs_item_guid'
                //console.log(`from cloneNode - cloning a VARIANT at assignedSortOrder ${assignedSortOrder}`);
                this.updateVariantSortOrder(null, assignedSortOrder, clonedVariantData); // this can update or insert new variants at specified sort_order, and will deal with all siblings too
                // it uses applyTransaction() internally, which should handle all refreshing
            } 
            // DEAL WITH CLONING ENTIRE GROUP
            if (cloneGroup) {
                // NOTE: rowNode in arg will always be a variant... need to get its parent qsItem first
                // determine correct new sort_order (for QsItem) --> make a NEW QsItem & add to context & update all qsItem sort_orders --> clone all variants & assign new qs_item_guid --> add rows to grid
                let qsItemToClone = this.getQsItem(rowNode.data.qs_item_guid);
                let newQsItemSortOrder = placeAbove ? qsItemToClone.sort_order : qsItemToClone.sort_order + 1;
                let newQsItem = qsItemToClone.clone({sort_order: 0});  // clone with a 'dummy' sort_order, then updateQsItemSortOrder to assign new sort_order to it and all siblings
                // single function to add it to context and update all sort_orders
                this.updateQsItemSortOrder({
                    newQsItemObj: newQsItem, 
                    newSortOrder: newQsItemSortOrder, 
                    skipRefresh: true
                });  // will update all sibling sort_order too, but skip doing refresh here, wait until we add variants
                // now clone all variants in the group - get them from rowNode.parent.childrenAfterSort -- loop through
                // must assign qs_item_guid to the NEW qsItem.guid -- but we can re-use the existing sort_order on each
                let clonedVariantsData = [];  // array of QuoteSheetItemVariants (not rowNodes)
                rowNode.parent.childrenAfterSort.forEach(childNode => {
                    clonedVariantsData.push(childNode.data.clone({qs_item_guid: newQsItem.guid}));  // makes new instances of QuoteSheetItemVariant, but keep existing sort_order
                })
                // now add variants to grid -- no need to update sort_order on variants, since they're all new, and we just copied sort_order from the cloned objects
                this.addRowsToGrid({newDataRows: clonedVariantsData});  // this should handle all refreshing needed & set focus on first new variant row
            }
            // finally, you must redraw all affected rows (that weren't already refreshed in other methods) to update row classes, etc.
            this.redrawGroup(rowNode);
            //this.gridApi.redrawRows({rowNodes: affectedRowNodes});
            this.setDirty();
        },
        addVariant: function(rowNode, position) {
            // add a new BLANK variant to the same (existing) group as rowNode
            // position (String) -- options are:
            //  - 'above' -- place just ABOVE rowNode
            //  - 'below' -- place just BELOW rowNode -- this is the DEFAULT
            //  - 'end' -- place at the END of the group that rowNode is in
            if (rowNode.group) {  // make sure this isn't somehow a dummy group node
                console.error('From addVariant, this only handles adding from Variant rowNodes, not dummy group rowNodes');
                return;  // just return now if we find this problem
            }
            //let affectedRowNodes = [rowNode];  // add things here as you go, will redrawRows on these at the end
            const allowedPositions = ['above', 'below', 'end'];
            let assignedSortOrder = rowNode.data.sort_order + 1;  // the is the default (position === 'below')... if we have valid 'position' arg, then maybe change it
            if (position && allowedPositions.includes(position)) {
                if (position === 'above') {
                    assignedSortOrder = rowNode.data.sort_order;
                } else if (position === 'end') {
                    assignedSortOrder = rowNode.parent.allChildrenCount + 1;
                }
            }
            console.log('addVariant for rowNode.data', rowNode.data);
            // you can pass initialData object to makeBlankVariant -- we'll use variant data from the existing first row as the initialData
            // you could also use .clone() method on QuoteSheetItemVariant class... you use that when you clone a variant
            const initialData = cloneDeep(rowNode.data) ?? {};  // in case it was somehow a group node, which doesn't have data
            // remove unique things and set sort_order -- maybe no need to set sort_order, will get set when we insert it below
            initialData['id'] = '';  // just blank this
            delete initialData.guid;  // must completely delete it, not just unset it, or else a new one won't get set
            //let newVariantObj = makeBlankVariant({qs_item_guid: rowNode.data.qs_item_guid, sort_order: assignedSortOrder}); 
            const defaultCurrency = this.quoteSheetData?.currency || '';
            initialData['default_currency'] = defaultCurrency;  // just in case, we'll pass the default_currency too, which is only used as fallback
            let newVariantObj = makeBlankVariant(initialData);
            //console.log(`from addVariant - adding a VARIANT at assignedSortOrder ${assignedSortOrder}`);
            this.updateVariantSortOrder(null, assignedSortOrder, newVariantObj); // this can update or insert new variants at specified sort_order, and will deal with all siblings too
            // it uses applyTransaction() internally, which should handle all refreshing
            // run validation -- must do this before redrawing rows
            this.redrawGroup(rowNode);
            // finally, you must redraw all affected rows (that weren't already refreshed in other methods) to update row classes, etc.
            //this.gridApi.redrawRows({rowNodes: affectedRowNodes});
            this.setDirty();
        },
        deleteNode: function({rowNode, group = false}) {
            // delete a rowNode -- if group = true, then delete the entire group it is part of, even if multiple rowNodes
            // this DOES NOT SHOW CONFIRMATION -- do that before calling this (like from popover in component renderer)
            // does the remove via applyTransaction(remove) 
            // FIRST -- check if the rowNode is the only one in group... if so, make sure we do a group delete
            if (!group && rowNode.parent.allChildrenCount === 1) {
                // if we didn't ask for group delete, but rowNode is an only child, we'll force group = true so we delete the QsItem too
                group = true;
            }
            // make array of all node IDs that you'll delete... need to remove them from invalidNodeIdMap when done
            let deletedNodeIds = [];
            if (group) { // for GROUP REMOVE -> remove all variants, then remove QsItem from context, then reindex qsItems
                let qsItemGuid = rowNode.data.qs_item_guid;
                let allVariantNodes = rowNode.parent.childrenAfterSort;
                let allVariantNodeIds = allVariantNodes.map(vn => vn.id);
                deletedNodeIds = [rowNode.id, ...allVariantNodeIds];
                this.deleteVariantNodes({rowNodes: allVariantNodes});  // this will remove, update any remaining siblings, applyTransaction, and redraw any remaining
                this.removeQsItemFromContext(qsItemGuid);  // this removes the qsItem AND reindexes sort_order on all the remaining ones
            } else {  // for VARIANT REMOVE
                this.deleteVariantNodes({rowNodes: [rowNode]});  // does the remove, updates remaining siblings sort_order & redraws them
                deletedNodeIds = [rowNode.id];            
            }
            // NEW - July 25 2022 - must also remove this node/key (and children!) from the invalidNodeIdMap (if they were there, or else quote sheet will get stuck in permenent error state!)
            // doing this in our strage way because Maps are not reactive.... so rebuild the map and replace old one - there's probably a better way
            let newInvalidMap = new Map();
            this.invalidNodeIdMap.forEach((value, nodeId) => {  // Map.forEach args are (value, key, map)
                if (!deletedNodeIds.includes(nodeId)) {
                    newInvalidMap.set(nodeId, value);
                }
            });
            this.invalidNodeIdMap = newInvalidMap;
            this.updateHasInvalidRows();  // must call this after modifying invalidNodeIdMap still....
            this.setDirty();
        },
        deleteVariantNodes: function({rowNodes}) {
            // delete an array of VARIANT rowNodes from a SINGLE ITEM/GROUP (pass in an array)
            // removes via applyTransaction, and also restacks/reindexes sort_order for any remaining variants in the group
            // ONLY USE FOR VARIANTS IN ITEMS/GROUPS WITH MULTIPLE VARIANTS - DOES NOT DELETE THE PARENT qsItem
            // get all sibling nodes in the group, separate into delete or keep so we can update sort_order on any keepers at the same time
            let deleteNodeIds = rowNodes.map(node => node.id);  // make array of IDs we want to delete so we can separate into keep/delete
            let allSiblingNodes = rowNodes[0].parent.childrenAfterSort;
            let nodesToDelete = [];  // these are rowNodes, not data
            let nodesToKeep = [];  // these are rowNodes, not data
            allSiblingNodes.forEach(siblingNode => {
                if (deleteNodeIds.includes(siblingNode.id)) {
                    nodesToDelete.push(siblingNode);
                } else {
                    nodesToKeep.push(siblingNode);
                }
            });
            // now make arrays of DATA to pass to applyTransaction() - put in txnData object
            let dataToRemove = nodesToDelete.map(removeNode => removeNode.data);
            let txnData = {remove: dataToRemove};
            if (nodesToKeep.length) {
                let rawDataToKeep = nodesToKeep.map(keepNode => keepNode.data);  // must reindex sort_order on these
                let resortedDataToKeep = this.reindexVariantsInGroup(rawDataToKeep);
                txnData['update'] = resortedDataToKeep;
            }
            // make the update all at once, one transaction
            let txnResult = this.gridApi.applyTransaction(txnData);
            //console.log(`Removed ${txnResult.remove.length} rowNodes`);
            let updatedNodes = txnResult.update;
            // refresh: redrawRows for any we updated
            if (updatedNodes.length) {
                // just re-validate and redraw for any in the group -- get the first rowNode and use that as arg
                this.redrawGroup(updatedNodes[0]);
                //this.gridApi.redrawRows({rowNodes: updatedNodes});
            }
            this.setDirty();
        },
        showItemPicker: function() {
            this.$refs.itemPickerModal.showModal();
        },
        addSavedItem: function(productObj) {
            // called from 'add-item' event on itemPickerModal
            // add linked_product to context --> make a new QsItem with linked_product --> add to context, then make a blank Variant --> add row to grid
            this.addLinkedProductToContext(productObj);  // this instantiates a new class instance of LinkedProduct
            let qsItemsInContext = this.gridContext.qsItemMap.size;
            let newQsItemSortOrder = qsItemsInContext + 1;  // add to end/bottom
            let newQsItem = makeBlankQsItem({
                quotesheet_id: this.quoteSheetId, 
                sort_order: newQsItemSortOrder,  // maybe this isn't needed if you're inserting at specific sort_order next...
                linked_product: productObj,  // will grab linked_product_id from this
                use_pn_for_name: this.usePnForName,  // may not be used anymore
            });
            this.updateQsItemSortOrder({
                newQsItemObj: newQsItem, 
                newSortOrder: newQsItemSortOrder, 
                skipRefresh: true,  
            });  // this does the insert into context, and reindexes all sort_orders.  skip refresh, and call it at end of this function if needed
            // NOTE: we MAY have target_price / current_price saved on the linked item... but it might not be set to the same currency as the quote sheet currency... that breaks some assumptions we have here (we assume all target/current prices on a quote sheet use the quote sheet currency, for mostly good reasons)
            // so we are deciding to NOT copy target_price/current_price from linked product to new qsItem
            // theoretically, we could inspect the currency, see if they're the same, and use them if that's the case... can implement that later if needed
            const defaultCurrency = this.quoteSheetData?.currency || '';
            let newVariantObj = makeBlankVariant({quotesheet_item: newQsItem, sort_order: 1, default_currency: defaultCurrency});
            this.addRowsToGrid({newDataRows: [newVariantObj]});
            // any other refresh/redraw?  if so, add it to addBlankRow too
            this.setDirty();
        },
        // REFRESH / REDRAW THINGS
        redrawRow: function(rowNode) {
            // helper utility to redraw a SINGLE rowNode
            this.gridApi.redrawRows({rowNodes: [rowNode]});
        },
        redrawAllRows: function() {
            this.gridApi.redrawRows();
        },
        redrawGroup: function(rowNode) {
            // combined method to run validation on all rows/siblings in a group, then redrawRows on all those rows also
            // pass in a single rowNode, it will act on all rowNodes in its parent item group (siblings)
            // NEW - we will also refresh data in these rows before calling redraw, so we can use this more places
            let allSiblings = rowNode.parent.childrenAfterSort;
            this.validateRowNodes(allSiblings);
            this.refreshRows({rowNodes: allSiblings, flash: false});
            this.gridApi.redrawRows({rowNodes: allSiblings});
        },
        forceGridRefresh: function(showFlash) { // helper method to force entire grid to refreshCells -- not redrawRows
            // by default we will suppressFlash: true -- but if you pass showFlash arg as true, we won't suppress it
            let refreshParams = {
                //rowNodes: [array of rowNodes],
                //columns: ['current_price', 'target_price'],
                force: true,
                suppressFlash: showFlash ? false : true,
            };
            this.gridApi.refreshCells(refreshParams);
        },
        refreshRows: function({rowNodes, flash = true}) {
            // wraps this.gridApi.refreshCells() with some defaults
            // with_redraw - boolean, default false, if true, we'll call redraw on these rows after refresh
            let refreshParams = {
                rowNodes: rowNodes,
                force: true,  // hard-coding this
                suppressFlash: flash ? false : true,
            };
            this.gridApi.refreshCells(refreshParams);
        },
        refreshRowModel: function() {
            //console.log('refreshClientSideRowModel');
            this.gridApi.refreshClientSideRowModel('group');
        },
        handleOverrideHasSubmittedReplies: function(checkedValue) {
            // handler for the switch for overrideHasSubmittedReplies
            // update the data (doing this instead of v-model on the switch)
            // and force off all editing on the grid
            // and forceGridRefresh to update cellRenderer components
            console.log(checkedValue);
            this.gridApi.stopEditing(true);
            this.overrideHasSubmittedReplies = checkedValue;
            this.setIsEditAllowed();
            // this.forceGridRefresh(false); - this is not enough, must redraw to render row drag handles
            this.redrawAllRows();
        },
        onGridReady: function(gridReadyParams) {
            // do stuff here that you need grid to be ready for, just another hook point
            console.log('GRID READY - gridReady params');
            console.log(gridReadyParams);
            //console.log('getModel');
            //console.log(this.gridApi.getModel());
            this.gridApi.sizeColumnsToFit();
            this.gridApi.expandAll();
        },
        dumpContext: function() {
            // dump the grid context
            console.log('this.gridContext');
            console.log(this.gridContext);
        },
        dumpGrid: function() {
            // just dump grid data to console for testing
            let gData = [];
            let allNodes = [];
            this.gridApi.forEachNode((node) => {
                allNodes.push(node);
                gData.push(node.data);
            });
            console.log('NODES:');
            console.log(allNodes);
            console.log('ROW DATA:');
            console.log(gData);
            console.log('getFirstDisplayedRow');
            console.log(this.gridApi.getFirstDisplayedRow());
            console.log('getLastDisplayedRow');
            console.log(this.gridApi.getLastDisplayedRow());
        },
        getCurrentRowData: function() {
            // utility to get all row data from the grid -- in case you won't use 2-way vue binding
            let gridData = [];
            this.gridApi.forEachNode((node) => {
                gridData.push(node.data);
            });
            return gridData;
        },
        updateRowHeights: function(params) {  // this will make ALL rows update row height... shotgun approach
            // can also use rowNode.setRowHeight(height) -- can pass null, undefined and grid will calculate it, 
            // then always must call api.onRowHeightChanged() to actually reposition the rows
            if (params && (params.finished !== false)) {  // check if finished, if params has that... it may not
                params.api.resetRowHeights();
            } else if (!params) {
                // but maybe we called from a place without passing in params... 
                this.gridApi.resetRowHeights();
            }
        },
        updateNodeRowHeight: function(rowNodeArray, height) {
            // pass in a SINGLE rowNode OR an ARRAY of rowNodes, optionally pass in a specific height to use
            // if no height, grid will calculate it
            // first, set new heights for each node --> then call onRowHeightChanged() to actually reposition the DOM
            let nodesToUpdate = Array.isArray(rowNodeArray) ? rowNodeArray : [rowNodeArray];  // make an array if only passed in one
            nodesToUpdate.forEach(node => {
                node.setRowHeight(height);  // height may be null, undefined, which is OK -- grid will calculate
            });
            this.gridApi.onRowHeightChanged();  // this actually updates the DOM
        },

        // ROW DRAG FUNCTIONS - NEW
        getRowDragText: function() {  // can take params
            // return a string to show when dragging this row -- call this from whatever colDef has rowDrag: true
            return 'DROP IN NEW LOCATION';
        },
        isParentItemDrag: function(dragEvent) {  // return Boolean:  true if this is drag for parent Item, false if this is drag for Variant
            // get the event path (an Array of DOM elements) --> go through elements until you find .ag-cell --> check classList to see if its variant or item
            //console.log('checking isParentItemDrag for event', dragEvent);
            let eventPathArray = getEventPath(dragEvent.event);  // returns an Array, maybe empty if browser does not support event.path or event.composedPath()
            //console.log('eventPathArray', eventPathArray);
            let dragCell = eventPathArray.find(ele => ele.classList?.contains('ag-cell'));  // ele.classList is a DOMTokenList, which has a .contains() method
            const parentItemDragClass = this.parentItemDragClass;  // this is the CSS class (string) applied to parent item column
            console.log('dragCell classList =', dragCell?.classList);
            if (dragCell && dragCell.classList.contains(parentItemDragClass)) {
                console.log('PARENT DRAG');
                return true;
            } else {
                console.log('VARIANT DRAG');
                return false;
            }
        },
        getDropData: function(dragEvent) {
            // return info about the possible drop location
            // get values from dragEvent
            //console.log('from getDropData');
            //console.log('dragEvent', dragEvent);
            let dragNode = dragEvent.node;
            let overNode = dragEvent.overNode;  // dragEvent.overNode might be null if we are not over a node!
            let yPos = dragEvent.y;
            // set our default return object
            let retObj = {
                isAllowed: false,  // is this an allowed drop location?
                highlightDropNode: null,  // IF its an allowed drop
                highlightDropNodeAbove: true,  // true if highlight top border, false if highligh bottom border
                // DO THIS LATER newSortOrder: null,  // the new sort_order to use to insert something relative to overNode's sort_order -- will be same as overNode's sort_order to insert above, or overNode's sort_order + 1 to insert below
            }
            // determine if this is allowed
            // March 29 2022 - if no overNode, its not allowed, just return retObj
            if (!overNode) {
                return retObj;
            }
            retObj.isAllowed = this.isParentDrag ? !!overNode : dragNode.parent.key === overNode.parent.key;  // parent drags can be dropped anywhere (as long as we have overNode), variants can only be dropped in their group
            if (!retObj.isAllowed) {  // if not allowed, return early with entire return object
                return retObj;
            }
            // otherwise, we'll continue to determine highlight nodes, etc.
            let firstNodeInZone = this.isParentDrag ? overNode.parent.childrenAfterSort[0] : overNode;
            let lastNodeInZone = this.isParentDrag ? overNode.parent.childrenAfterSort.slice(-1)[0] : overNode;
            let dropZoneStartY = firstNodeInZone.rowTop; 
            let dropZoneEndY = lastNodeInZone.rowTop + lastNodeInZone.rowHeight;
            let dropZoneMidpointY = ((dropZoneEndY - dropZoneStartY) / 2) + dropZoneStartY;
            // NOTE: for all math here, we'll have starting values be inclusive, ending values be exclusive
            if (yPos >= dropZoneStartY && yPos < dropZoneMidpointY) {
                retObj.highlightDropNode = firstNodeInZone;
                retObj.highlightDropNodeAbove = true;
            } else if (yPos >= dropZoneMidpointY && yPos < dropZoneEndY) {
                retObj.highlightDropNode = lastNodeInZone;
                retObj.highlightDropNodeAbove = false;
            }
            //console.log(`yPos: ${yPos} -- startY: ${dropZoneStartY} -- midpointY: ${dropZoneMidpointY} -- endY: ${dropZoneEndY}`);
            // just for testing, let's console.log the sort_order of highlightDropNode -- don't always do this... lookups in context could be expensive, and don't need it until the end of drop anyways
            /* let overSortOrder = this.isParentDrag ? this.getQsItem(retObj.highlightDropNode.data.qs_item_guid).sort_order : retObj.highlightDropNode.data.sort_order;
            let newSortOrder = retObj.highlightDropNodeAbove ? overSortOrder : overSortOrder + 1;
            console.log(`Over sort_order: ${overSortOrder} -- NEW sort_order: ${newSortOrder}`); */
            return retObj;
        },
        resetDragDropData: function(event) {  // OPTIONALLY pass in a dragEvent from the grid, if you do, we'll reset all those nodes as well
            // DRY way of resetting row drag data - clear everything and redraw this.highlightDropNode if you had it populated
            let nodesToRedraw = [];
            if (event) {
                if (event.node) {nodesToRedraw.push(event.node)}
                if (event.overNode) {nodesToRedraw.push(event.overNode)}
            }
            if (this.highlightDropNode) {nodesToRedraw.push(this.highlightDropNode)}
            //this.possibleDropNode = null;
            this.isParentDrag = false;
            //this.dragType = '';
            this.highlightDropNode = null;
            this.highlightDropNodeAbove = true;
            if (nodesToRedraw.length) {
                this.gridApi.redrawRows({rowNodes: nodesToRedraw});
            }
        },
        onRowDragEnter: function(dragEvent) {
            // fired ONCE when the drag starts - determine if we are dragging Item (parent) or Variant
            //console.log('rowDragEnter');
            //console.log(dragEvent);
            this.isParentDrag = this.isParentItemDrag(dragEvent);
            //this.dragType = this.isParentItemDrag(dragEvent) ? 'parent' : 'variant';  // can also be '', reset to that on drag leave/end
        },
        onRowDragMove: function(event) { // NEW
            // - determine/set the potential drop row/node, highlight above/below, and potential new sort_order - call getDropData
            // - redraw/refresh rows as you go
            let nodesToRedraw = [];
            if (this.highlightDropNode) {  // if we had previously set highlightDropNode, add that here before changing it
                nodesToRedraw.push(this.highlightDropNode);
            }
            let { isAllowed, highlightDropNode, highlightDropNodeAbove } = this.getDropData(event);
            if (isAllowed && highlightDropNode) {  // if we have a valid drop
                if (this.highlightDropNode && this.highlightDropNode.id !== highlightDropNode.id) {  // if the highlightDropNode changed... maybe did not.  Saves some cycles by not doing too much redraw
                    nodesToRedraw.push(highlightDropNode);  // add this to redraw list 
                }
                this.highlightDropNode = highlightDropNode;  // set new local data (after adding to redraw list)
                this.highlightDropNodeAbove = highlightDropNodeAbove;
            } else {  // if we don't have a valid drop, then set local data to null/reset values
                this.highlightDropNode = null;  // should be no need to also 'reset' this.highlightDropNodeAbove
            }
            // now do the redraw (have to do redraw instead of refresh cells if you're changing rowClass instead of only cellClass)
            this.gridApi.redrawRows({rowNodes: nodesToRedraw});
        },
        onRowDragLeave: function(event) {
            // reset everything, just like you're stopping the drag -- will restart again if you go back in
            this.resetDragDropData(event);
        },
        onRowDragEnd: function(event) {
            let { isAllowed, highlightDropNode, highlightDropNodeAbove } = this.getDropData(event);
            if (isAllowed && highlightDropNode) {  // perform the moves
                if (this.isParentDrag) {  // perform PARENT move -- get data, update things, refresh grid
                    let qsItemGuid = event.node.data.qs_item_guid;  // get the ID of the QuoteSheetItem being dragged (each node will have one... the FK)
                    let currentDropNodeSortOrder = this.getQsItem(highlightDropNode.data.qs_item_guid).sort_order;
                    let newSortOrder = highlightDropNodeAbove ? currentDropNodeSortOrder : currentDropNodeSortOrder + 1;
                    this.updateQsItemSortOrder({qsItemGuid: qsItemGuid, newSortOrder: newSortOrder, skipRefresh: false});
                    // MOVED TO FUNCTION this.gridApi.refreshClientSideRowModel('group');
                } else {  // perform VARIANT move -- get data for new position, update data for this variant and all siblings, applyTransaction(update), refresh grid 
                    let draggedVariantGuid = event.node.data.guid;  // node ID is the same as variant guid, so we could also get event.node.id
                    let variantDropNodeSortOrder = highlightDropNode.data.sort_order;  // sort order of the overNode
                    let draggedNewSortOrder = highlightDropNodeAbove ? variantDropNodeSortOrder : variantDropNodeSortOrder + 1;
                    // pass things off to our updateVariantSortOrder utility function, which updates this and all sibling sort_order, AND does applyTransaction
                    this.updateVariantSortOrder(draggedVariantGuid, draggedNewSortOrder);
                }
                this.setDirty();  // only do this if we actually performed the moves
            } 
            this.resetDragDropData(event);  // reset everything when we're done
        },
        
        // NEW ROW / NODE UTILITY FUNCTIONS
        setIsEditAllowed: function() {
            let allowed = !this.hasSubmittedReplies || (this.hasSubmittedReplies && this.overrideHasSubmittedReplies);  
            this.isEditAllowed = allowed && !this.readOnly;  // Nov 9 2022 - added readOnly to allow parent to set readOnly if user does not have permission to edit, etc.
            this.gridApi.setSuppressRowDrag(!allowed);
        },
        hasLinkedProduct: function(params) {
            // does this node have a linked product on QuoteSheetItem?
            return params.data ? this.getQsItem(params.data.qs_item_guid).hasLinkedProduct : false;  // get the QuoteSheetItem and use its getter
        },
        isDataNode: function(params) {
            // simple boolean check to see if a node is a real data node, or a 'dummy' group row node (node.group = true)
            return !params.node.group;
        },
        isFirstVariant: function(params) {
            return params.node.firstChild;
        },
        isLastVariant: function(params) {
            return params.node.lastChild;
        },
        hasOneVariant: function(params) {
            return params.node.parent.allChildrenCount === 1;
        },
        hasMultipleVariants: function(params) {
            return params.node.parent.allChildrenCount > 1;
        },
        showCellInvalid: function(params) {
            // used in cellClassRules in colDefs -- DO NOT get things from invalidNodeIdMap -- that's updated too late
            // get all things from params
            let rowInvalidFields = this.getInvalidFields(params.node);
            return rowInvalidFields && rowInvalidFields.includes(params.colDef.field);
            //return this.invalidNodeIdMap.has(params.node.id) && this.invalidNodeIdMap.get(params.node.id).includes(params.colDef.field);
        },
        onCellValueChanged: function(eventParams) {
            // do stuff when a cell is edited, like validate the row, update the row height, etc.
            //console.log('cell value changed, eventParams:');
            console.log(eventParams);
            this.validateSingleNode(eventParams.node);
            //this.redrawRow(eventParams.node);
            let colIdsToUpdateRowHeight = ['item_name_nested', 'part_number', 'description'];  // only update row height for these cols, otherwise it interferes with auto edit widget opening (for selects, etc.)
            if (colIdsToUpdateRowHeight.includes(eventParams.column.colId)) {
                this.updateNodeRowHeight(eventParams.node, null);  // it can take a single rowNode, or array of rowNodes
            }
            this.setDirty();  // set isDirty from here
        },
        onDataModelChanged: function(rowData) { // rowData is passed in (data, not nodes)
            // rowData here will include dummy group rows too -- value of those will just be undefined, not an object
            // triggered from data-model-changed event from the grid
            //this.updateRowHeights();  // removed... just update the single rowNode using onCellValueChanged
            console.log('Data model changed!');
            //console.log(rowData);
            //this.currentGridData = rowData;  // not needed, so removing to reduce memory footprint
            this.updateCounts(rowData);
            // do not setDirty here... it gets called immediately after onFirstDataRendered.... so then it would always be dirty
        },
        onFirstDataRendered: function(event) {
            // fired the first time data is rendered into the grid
            //console.log('onFirstDataRendered');
            console.log(event);
            this.canSetDirty = true;  // must set this to true so setDirty() is not blocked
            this.isDirty = false;  // this is the starting point
            this.gridApi.sizeColumnsToFit();
        },
        setDirty: function(event) {  // MUST USE THIS and ONLY THIS to set this.isDirty = true
            console.log('from setDirty, maybe we have event:');
            if (event) {console.log(event)}
            if (!this.canSetDirty) {
                console.log('canSetDirty is false');
                return;
            }  // SKIP setting this.isDirty if initial data is not done
            console.log('we WILL set isDirty = true');
            this.isDirty = true;  // call this from many spots
            // call when row-data-changed? (NO), row-data-updated (applyTransaction), cell-value-changed, updateColDefs, and drag end... should be enough?
        },
        updateHasVariants: function(rowData) {
            // call this from multiple places, checks if hasVariants was true before, and whether it should be now based on current grid data
            // if it should change, then set new value for this.hasVariants, and updateColDefs 
            // pass in rowData -- ARRAY of DATA objects in rowNodes (rowNode.data)
            let beforeHasVariants = this.hasVariants;
            let nonGroupRowData = rowData.filter(rowObj => !!rowObj);
            this.hasVariants = !this.checkUnique(nonGroupRowData.map(row => row.qs_item_guid));  // many ways to do this, most reliable is probably checking if any rows have same qs_item_guid
            if (this.hasVariants !== beforeHasVariants) {
                // if changed, then must update col defs
                this.updateColDefs();
            }
        },
        updateCounts: function(rowData) {
            // call when grid data is updated, it updates local counts -- rowData will include dummy group rows!
            // Aug 6 2022 - change this to IGNORE empty rows/items
            // OLD - includes empties: this.currentItemCount = this.gridContext.qsItemMap.size;
            // NEW - ignores empties
            const allQsItems = this.getAllQsItems();
            this.currentItemCount = allQsItems.filter(qsi => !qsi.isEmpty()).length;
            //const rowData = this.getCurrentRowData();  // if you wono't pass it in...
            let nonGroupRows = rowData.filter(rowObj => !!rowObj);
            this.currentVariantCount = nonGroupRows.filter(rd => !this.isRowEmpty({rowData: rd})).length;
            this.updateHasVariants(nonGroupRows);  // need to pass on the full set of rows (at least full set of variants) not just the ones with data... althought result should be the same
            // update linkedProductIds so we can pass that to add saved product modal to know what's already added
            this.linkedProductIds = allQsItems.map(qsi => qsi.linked_product_id).filter(lpid => !!lpid);  // get array of all qsItems, get linked_product_ids (might be empty), filter out empty ones
        },
        onColumnVisible: function(params) {
            params.api.resetRowHeights();
        },
        getCurrentVendorColsEnabled: function() {
            // DRY helper for 2 methods below.... returns a SET of vendor col keys that are enabled from this.quoteSheetData
            let enabledVendorColKeys = [];  // list of keys for cols that are shown -- build this and return it (as a SET)
            // do not directly loop through Object.entries(this.quoteSheetData.vendorCols... since you might have other stuff in there like choice lists)
            // instead, loop through this.vendorColsAll, then get data from this.quoteSheetData.vendorCols
            this.vendorColsAll.forEach(vcol => {  // vcol.enableKey will be things like 'enable_price', 'enable_moq', etc...
                // now look at this.quoteSheetData.vendorCols[vcol.enableKey] to see which are turned on
                let vcolEnabled = this.quoteSheetData.vendorCols[vcol.enableKey];  // returns a real boolean (true/false, not on/off)
                if (vcolEnabled) {
                    enabledVendorColKeys.push(vcol.enableKey);  // add 'enable_price', 'enable_moq', etc. to our list
                }
            });
            /* Object.entries(this.quoteSheetData.vendorCols).forEach(([colKey, enabled]) => {
                if (enabled) {
                    enabledVendorColKeys.push(colKey);
                }
            }); */
            return new Set(enabledVendorColKeys);  // store it on the instance data
        },
        startColumnsEdit: function() {
            // call when we start/open settingsEditActive -- stash the current columns on/off settings so we can track what changed
            // if a NEW col was shown, use other method to turn on all checkboxes for that col
            // if a col was hidden, don't worry about updating values... they'll get overriden when saving anyways
            this.quoteSheetDataBeforeEdit = cloneDeep(this.quoteSheetData);  // clone it and stash it so you can reset if cancel without saving
            this.enabledVendorColKeysBeforeEdit = this.getCurrentVendorColsEnabled();  // use our helper, store it on the instance data
            this.settingsEditActive = true;
        },
        finishColumnsEdit: function() {
            console.log('finishColumnsEdit called');
            // called when we close/exit settingsEditActive 
            // TODO - better way -- instead of cloning quoteSheetData on start, then mutating the 'active' quoteSheetData, do it like this:
            //      - clone quoteSheetData, let that be the one you mutate with form fields, then IF you save it, take that one and replace the 'active' one with that
            this.gridBusy = true;
            let vendorColKeysAfterEdit = this.getCurrentVendorColsEnabled();  // our DRY helper, returns a SET
            let newColsEnabled = new Set();
            vendorColKeysAfterEdit.forEach((colKey) => {
                if (!this.enabledVendorColKeysBeforeEdit.has(colKey)) {
                    newColsEnabled.add(colKey);
                }
            });
            console.log('Turning on these cols:');
            console.log(newColsEnabled);
            if (newColsEnabled.size > 0) {
                this.enableVendorCols(newColsEnabled);  // pass Set to our helper -- this will not update colDefs... so do that here
            }
            // deal with any changes to CURRENCY
            this.setRowCurrency(this.quoteSheetData.currency);  // if you made any changes, you mutated this object
            this.updateColDefs();  // moved here instead of enableVendorCols so you can use it for both col types
            // for updating use_pn_for_name, we put it inside updateColDefs
            // this.validateAll();  //  <-- now included in this.updateColDefs()
            this.settingsEditActive = false;  // exit edit mode
            this.enabledVendorColKeysBeforeEdit = new Set();  // reset this to be empty Set
            this.gridBusy = false;
            // TODO - need to refresh the grid, so it can re-render all rows with new colDefs, etc.
            this.forceGridRefresh();
        },
        cancelColumnsEdit: function() {
            // clean up and reset things when canceling out of edit
            this.quoteSheetData = cloneDeep(this.quoteSheetDataBeforeEdit);  // clone it when setting back to be safe?
            // Aug 9 2022 - removing this... this.updateColDefs();  // do we need this? -- certainly safer
            // this.validateAll();  //  <-- now included in this.updateColDefs()
            this.settingsEditActive = false;
            this.enabledVendorColKeysBeforeEdit = new Set();  // reset this to be empty Set
        },
        enableVendorCols: function(colKeySet) {
            // used to TURN ON (enable) new vendor cols (supports multiple cols)
            // this first turns on checkbox for all rows in grid for array of colKeys, then updates the colDefs to turn on those cols
            // pass in an ARRAY or SET of colKeys, even if its just one element
            // THIS WILL OVERWRITE ALL ROWS TO HAVE COLUMN ENABLED (CHECKBOX = TRUE) SO ONLY PASS COLS INTO THIS THAT ARE NEWLY ENABLED, OR ELSE YOU'LL OVERWRITE PREVIOUS USER SELECTIONS
            var rowDataToUpdate = [];  // build array of DATA to update, not rowNodes
            this.gridApi.forEachNode((rowNode) => {
                if (!rowNode.group) { // only update rows that have variant data (not dummy groups)
                    let variantData = rowNode.data;  // the QuoteSheetItemVariant instance
                    colKeySet.forEach(colKey => {  // loop through each colKey, since this supports multiple
                        // OLD WAY WITH STRINGS:  nodeData.variant[colKey] = 'on';  // do it via setter or method on GridRow instance instead?
                        // OLD nodeData.variant[colKey] = true;  // do it via setter or method on GridRow instance instead?
                        variantData[colKey] = true;  // enable this vendor col
                    });
                    rowDataToUpdate.push(variantData);  // should be array of data, not rowNodes
                }
            });
            var updateResult = this.gridApi.applyTransaction({ update: rowDataToUpdate });
            console.log(updateResult);
            // now remake the colDefs and set them
            //this.updateColDefs();  
        },
        setRowCurrency: function(curCode) {  // curCode should be 'USD', 'EUR', etc.
            console.log('Setting row currency to: ' + curCode);
            // call this if you change the currency on price sheet -- may have to change this when you finally implement target/current prices
            // doing at least this much for now so you don't get errors for currency mismatch in backend serializer
            // loop through all variants, look at target_price_currency, current_price_currency, if different, then change currency and blank the currency amounts
            this.gridApi.forEachNode((rowNode) => {
                if (!rowNode.group) {
                    let variantData = rowNode.data;
                    variantData.setCurrency(curCode);  // class instance method -- it takes care of returning if not a variant, setting new currency, and nulling money values if needed
                }
            });
        },
        onGridColumnsChanged: function(event) {
            // triggered when the list of grid columns changed -- we'll use to set auto width
            //console.log('grid-columns-changed');
            console.log(event);
            this.gridApi.sizeColumnsToFit();  // this likely triggers 'column-resized' event, which calls updateRowHeights, so make sure we don't have infinite loop
        },
        updateColDefs: function() {
            // call this when changing what columns to show from user selections -- this sets new colDefs on grid
            // make sure this.quoteSheetData is already updated before calling this
            console.log('Updating Col Defs (updateColDefs)');
            this.updateGridContext();  // make sure our grid context is up to date
            // run validation -- moving this before setColumnDefs() since that triggers redraw of all rows, so we can update all rowClasses, etc.
            this.validateAll();  // this calls .isValid() on each data row, needed because updating which cols are shown can affect if lines are valid - but should we put BEFORE setColumnDefs() because that triggers redraw of rows???
            let newColDefs = this.buildColDefs();
            // before setting the new colDefs, we'll see what cols we have enabled.
            // if we changed item 'name' visibility, we need to update all rowData accordingly
            this.usePnForName = !this.quoteSheetData.internalCols.show_name;  // if not showing name, use P/N
            // NO LONGER NEEDED Feb 23 2023? this.getAllQsItems().forEach(qsi => qsi.setUsePnForName(this.usePnForName));
            // now we can update columns
            this.gridApi.setColumnDefs(newColDefs);  // this is how ag-grid example does it, even though they have :columnDefs="columnDefs" vue binding
            // from docs on setColumnDefs():  "The grid will redraw all the column headers, and then redraw all of the rows."
            this.setDirty();
            //this.forceGridRefresh();  // we should not need this if setColumnDefs() really does trigger redraw of all rows
            // will get called from grid-columns-changed event instead - this.gridApi.sizeColumnsToFit(); // this will trigger event, which triggers update row heights also
        },
        buildVendorResponseColDefs: function() {
            // call this inside your main buildColDefs function - return array of colDefs in Ag-grid format
            // get vendorColsAll from import  (the canonical list you use for QuoteSheetBuilder AND QuoteSheetReply components)
            
            const buildCellClasses = (fieldName, isAtLeastOneRequired, alignment, extraClassesArray) => {  
                // fieldName & alignment are strings, extraClassesArray is ARRAY of strings
                // isAtLeastOneRequired ==> BOOLEAN -- is this col included in 'at least one required' validation check?  used to highlight that at least one of these checkboxes is required
                // CALL THIS when building colDefs to build array of STATIC CSS CLASSES for this col 
                // don't pass the function to colDefs, but pass the RETURN of this function
                // return an ARRAY of classes -- you _could_ return a function to apply classes dynamically, but no need right now
                let allCellClasses = ['justify-content-around', 'vendor-response-col'];  // build this up - start with 'justify-content-around' because cells are flex with your theme, you want centered
                if (alignment) {
                    allCellClasses.push('column-align-' + alignment);
                } 
                if (isAtLeastOneRequired) {
                    allCellClasses.push('response-data-one-required');
                }
                if (fieldName) {
                    allCellClasses.push('vendor-response-col-' + fieldName);  
                }
                if (!!extraClassesArray && Array.isArray(extraClassesArray) && extraClassesArray.length) {  // important to check all these things, in this order
                    allCellClasses = allCellClasses.concat(extraClassesArray);
                }
                return allCellClasses.length ? allCellClasses : null;  // return null if its an empty array
            };
            
            let vendorResponseColDefs = [];
            this.vendorColsAll.forEach(vcol => {

                let colDef = {
                    headerName: vcol.label, 
                    headerClass: buildCellClasses(vcol.enableKey, vcol.builderGrid.atLeastOneRequired, vcol.builderGrid.alignment, [vcol.builderGrid.headerClass]),  // builderGrid.headerClass is optional... should be a single string, or null
                    cellClass: buildCellClasses(vcol.enableKey, vcol.builderGrid.atLeastOneRequired, vcol.builderGrid.alignment, null),  // returns array of classes, or null if array is empty
                    field: vcol.enableKey, 
                    headerTooltip: vcol.builderGrid.tooltip,
                    minWidth: 50,
                    maxWidth: 120,
                    //type: ['variantData'],  // sets valueGetter, valueSetter, cellClassRules (to make parent row gray)
                    hide: (!this.quoteSheetData.vendorCols[vcol.enableKey] || vcol.enableKey === 'enable_quoted_price' || vcol.enableKey === 'enable_quoted_subtotal'),  // get current value of checkbox
                    cellRenderer: 'quoteLineCheckboxCellRenderer',  // tied to QuoteLineCheckboxCellRenderer via frameworkComponents
                    cellRendererParams: () => {  // must return an object -- this will update dynamically
                        return {
                            disabled: !this.isEditAllowed,
                        }
                    },
                    editable: () => this.isEditAllowed,  // must have this on to enable copy/paste, drag/fill handle
                    cellEditor: 'agRichSelectCellEditor',  // turning off cell editor would be best (just allow our custom checkbox thing) but this works for now
                    cellEditorParams: {
                        //values: ['on', 'off'],
                        values: [true, false],
                        //values: ['Enabled', 'Disabled'],
                    },
                    suppressPaste: true,  // to prevent problems pasting non-bool stuff into this col
                    valueFormatter: (params) => {  // convert true/false to 'Enabled' / 'Disabled'
                        if (params.value === true) {
                            return 'Enabled';
                        } else {
                            return 'Disabled';
                        }
                    },
                    valueParser: (params) => {  // convert 'Enabled' / 'Disabled' to true/false
                        if (params.newValue === 'Enabled') {
                            return true;
                        } else {
                            return false;
                        }
                    },
                };
                vendorResponseColDefs.push(colDef);
            });
            return vendorResponseColDefs;
        },
        buildColDefs: function() {
            let newColDefs = [
                {   // NEED THIS - this is how row grouping is actually done -- by groupId, which is the ID of the ITEM for each line
                    // hide: true // rowGroup: true  -- don't need to show it, but need it for grouping since we have all info on each line, and we have hide open parents
                    // doing it this way, each ROW in the grid is actually a VARIANT, hold the ITEMs in the grid CONTEXT?
                    headerName: 'Item ID',
                    field: 'qs_item_guid',
                    hide: true,
                    rowGroup: true,
                    //rowDrag: true,
                    headerClass: 'pl-3',
                    cellRenderer:'agGroupCellRenderer',
                    cellRendererParams: {
                        suppressCount: true,
                        checkbox: false,
                    },
                },
                {
                    headerName: '',
                    pinned: true,
                    colId: 'actions',
                    //field: 'actions',
                    //rowDrag: this.isTopLevelRow,
                    rowDrag: true,
                    wrapText: true,
                    autoHeight: true,
                    //rowDragText: this.getRowDragText,
                    type: ['firstChildVisible'],
                    suppressCellFlash: true,
                    cellRenderer: 'actionButtonComponent',
                    cellClass: [this.parentItemDragClass, 'bg-gray-200'],  // this.parentItemDragClass is important so we know rowDrag from this column is a GROUP-LEVEL drag, 'ag-cell-flex-between'
                    headerClass: 'text-center',
                    minWidth: 65,
                    maxWidth: 65,
                },
                {
                    headerName: 'Item Details',
                    headerGroupComponent: 'titleSubtitleHeaderGroupRenderer',
                    headerGroupComponentParams: {
                        subtitle: 'Provide as much detail as needed for vendors.',
                    },
                    marryChildren: true,
                    children: [
                        {
                            headerName: 'Item Name',
                            field: 'item_name',
                            editable: (params) => {
                                if (!this.isEditAllowed) {return false}
                                return this.isFirstVariant(params) && !this.hasLinkedProduct(params);
                            },
                            wrapText: true,
                            autoHeight: true,
                            type: ['firstChildVisible'],
                            hide: !this.quoteSheetData.internalCols.show_name,
                            minWidth: 100,
                            maxWidth: 400,
                            flex: 1,
                            cellClassRules: {
                                'cell-invalid': this.showCellInvalid,
                            },
                            valueGetter: this.qsItemContextGetter,  // have lots of additional logic IN the renderer component
                            valueSetter: this.qsItemContextSetter,
                            // valueParser: agStringParser,  // replaced by valueSetterParser
                            valueSetterParser: valueSetterParserString,  // NEW COMMON PARSER
                            cellRenderer: 'quoteSheetItemVueRenderer',
                            cellRendererParams: {
                                qsItemField: 'name',
                                linkedProductField: 'name',
                                placeholder: 'Item Name',
                            },
                        },
                        {
                            headerName: 'Part Number',
                            headerTooltip: 'If provided, will automatically save to Items database and link with any other RFx with same part number for internal use.',
                            field: 'item_part_number',
                            editable: (params) => {
                                if (!this.isEditAllowed) {return false}
                                return this.isFirstVariant(params) && !this.hasLinkedProduct(params);
                            },
                            type: ['firstChildVisible'],
                            hide: !this.quoteSheetData.internalCols.show_part_number,
                            minWidth: 100,
                            maxWidth: 400,
                            flex: 1,
                            valueGetter: this.qsItemContextGetter,  // have lots of additional logic IN the renderer component
                            valueSetter: this.qsItemContextSetter,
                            valueSetterParser: valueSetterParserString,  // NEW COMMON PARSER
                            // valueParser: agStringParser,
                            cellRenderer: 'quoteSheetItemVueRenderer',
                            cellRendererParams: {
                                qsItemField: 'part_number',
                                linkedProductField: 'part_number',
                                placeholder: 'Part Number',
                            },
                        },
                        {
                            headerName: 'Item Description',
                            headerTooltip: 'Description of this item',
                            field: 'item_description',
                            editable: (params) => {  
                                // we'll make item_description editable, even if linked product
                                // does not update linked_product, but just the qsItem
                                return this.isEditAllowed && this.isFirstVariant(params);
                            },
                            type: ['firstChildVisible'],
                            hide: !this.quoteSheetData.internalCols.show_description,
                            wrapText: true,
                            autoHeight: true,
                            minWidth: 100,
                            maxWidth: 400,
                            flex: 1,
                            valueGetter: this.qsItemContextGetter,  // have lots of additional logic IN the renderer component
                            valueSetter: this.qsItemContextSetter,
                            valueSetterParser: valueSetterParserString,  // NEW COMMON PARSER 
                            // valueParser: agStringParser,
                            /* 
                            // not using quoteSheetItemVueRenderer here... item_description should not pull from linkedProduct
                            // if you want to show other linkedProduct cols (description, mfg, etc.) have a way to add those cols (TODO later)
                            // but you need some column for adding an event-specific description
                            cellRenderer: 'quoteSheetItemVueRenderer',
                            cellRendererParams: {
                                qsItemField: 'description',
                                linkedProductField: 'description',
                                placeholder: 'Item Description',
                            }, */
                        },
                        {
                            headerName: 'Variant Description',
                            headerTooltip: 'Description of this quote variant.  (different quantity break, different packaging, etc.)',
                            field: 'description',
                            editable: (params) => {
                                return this.isEditAllowed && this.hasMultipleVariants(params);
                            },
                            hide: !this.hasVariants,
                            wrapText: true,
                            autoHeight: true,
                            minWidth: 100,
                            maxWidth: 400,
                            flex: 1,
                            rowDrag: this.hasMultipleVariants,
                            //cellRenderer: 'actionButtonComponent',
                            cellRendererSelector: (params) => {
                                const actionButton = {
                                    component: 'actionButtonComponent',
                                };
                                return this.hasMultipleVariants(params) ? actionButton : null;
                            },
                            valueGetter: (params) => {
                                return this.hasMultipleVariants(params) ? params.data[params.colDef.field] : '';  // return blank value if we only have one variant
                            },
                            valueSetter: this.qsVariantSetter,
                            // valueParser: agStringParser,
                            valueSetterParser: valueSetterParserString,  // NEW COMMON PARSER
                            cellClassRules: {
                                'cell-invalid': this.showCellInvalid,
                            },
                        },
                        {
                            headerName: 'Need by Date',
                            headerTooltip: 'Required delivery date for this item',
                            field: 'need_by_date',
                            editable: () => {  // (params)
                                return this.isEditAllowed; // && this.hasMultipleVariants(params);
                            },
                            hide: !this.quoteSheetData.internalCols.show_need_by_date,
                            wrapText: false,
                            autoHeight: true,
                            minWidth: 50,
                            maxWidth: 200,
                            flex: 1,
                            cellRenderer: 'vueDateButtonRenderer',
                            cellRendererParams: {
                                placeholder: 'Select...',
                            },
                            cellEditor: 'bootstrapVueDatePickerEditor', // OLD VERSION with v-calendar 'vueDatePickerEditor',
                            cellEditorParams: null,
                            singleClickEdit: false,  // because we have button to click instead... but maybe could still make it true
                            valueFormatter: (params) => {
                                //console.log('need_by_date valueFormatter params.value:', params.value);
                                //let dateInst = params.value ? new Date(params.value) : null; 
                                //console.log(dateInst);
                                //return dateInst ? dateInst.toLocaleDateString() : '' ;
                                // switched to use agDateFormatter, which just wraps FormatDateHelper, which uses Luxon?
                                return agDateFormatter(params);
                            },
                            valueSetter: this.qsVariantSetter,
                            valueSetterParser: valueSetterParserDate,  // NEW COMMON PARSER
                            // valueParser: null,  
                        },
                        {
                            headerName: 'Sort Order',
                            field: 'sort_order',
                            sort: 'asc', // NOTE - IMPORTANT -- this is what we're sorting by!
                            hide: true,
                        },
                    ],
                },
                {
                    headerName: 'Quantity',
                    headerGroupComponent: 'titleSubtitleHeaderGroupRenderer',
                    headerGroupComponentParams: {
                        subtitle: 'Quantity / UOM / etc.',
                    },
                    marryChildren: true,
                    children: [
                        {
                            headerName: 'Quantity',
                            field: 'quantity',
                            type: ['quantityCol'],
                            headerClass: ['column-align-right'],
                            cellClass: ['colgroup-quantity', 'column-align-right'],
                            /* cellClassRules: {  // these work, but are probably horrible for performance... use CSS instead... or just change BG colors -- SO MUCH EASIER
                                'cell-border-left': (params) => {
                                    console.log('params for cellClassRules');
                                    console.log(params);
                                    let thisCol = params.columnApi.getColumn(params.colDef.field);
                                    let thisColId = thisCol.colId;
                                    console.log(thisCol);
                                    let allGroupCols = thisCol.parent?.displayedChildren;
                                    let groupFirstChildColId = allGroupCols ? allGroupCols[0].colId : null;
                                    //let groupLastChildColId = allGroupCols ? allGroupCols[(allGroupCols.length - 1)].colId : null;
                                    return thisColId === groupFirstChildColId;
                                },
                                'cell-border-right': (params) => {
                                    console.log('params for cellClassRules');
                                    console.log(params);
                                    let thisCol = params.columnApi.getColumn(params.colDef.field);
                                    let thisColId = thisCol.colId;
                                    console.log(thisCol);
                                    let allGroupCols = thisCol.parent?.displayedChildren;
                                    //let groupFirstChildColId = allGroupCols ? allGroupCols[0].colId : null;
                                    let groupLastChildColId = allGroupCols ? allGroupCols[(allGroupCols.length - 1)].colId : null;
                                    return thisColId === groupLastChildColId;
                                },
                            }, */
                            minWidth: 60, 
                            maxWidth: 100,
                            flex: 1,
                            headerTooltip: 'Quantity',
                            hide: !this.quoteSheetData.internalCols.show_quantity,
                            valueFormatter: agNumberFormatter,
                            // valueParser: agNumberParser,
                            valueSetter: this.qsVariantSetter,
                            valueSetterParser: valueSetterParserNumber,
                        },
                        {
                            headerName: 'UoM',
                            field: 'uom',
                            type: ['quantityCol'],
                            cellClass: ['colgroup-quantity'],
                            minWidth: 60, 
                            maxWidth: 100,
                            flex: 1,
                            headerTooltip: 'Unit of Measure',
                            hide: !this.quoteSheetData.internalCols.show_uom,
                            //valueParser: agStringParser,
                            valueSetter: this.qsVariantSetter,
                            valueSetterParser: valueSetterParserString,  // NEW COMMON PARSER
                        },
                        {
                            headerName: 'Timeframe',
                            field: 'quantity_timeframe',
                            type: ['quantityCol'],
                            cellClass: ['colgroup-quantity'],
                            minWidth: 70, 
                            maxWidth: 120,
                            flex: 1,
                            cellEditor: 'agRichSelectCellEditor',
                            cellEditorParams: {
                                values: this.timeframeOptions,
                            },
                            valueSetter: (params) => {  // special one just for select/choices
                                return this.qsVariantSetterSelect(params, this.timeframeOptions);
                            },  
                            valueSetterParser: null,  // don't need this here.. our select setter takes care of it
                            //valueParser: agStringParser,
                            headerTooltip: 'Timeframe for Quantity',
                            hide: !this.quoteSheetData.internalCols.show_timeframe,
                        },
                    ],
                },
                {
                    headerName: 'Reference Prices',
                    headerGroupComponent: 'titleSubtitleHeaderGroupRenderer',
                    headerGroupComponentParams: {
                        subtitle: 'INTERNAL-ONLY.  Not shown to vendors.',
                    },
                    marryChildren: true,
                    children: [
                        {
                            headerName: 'Current Price', 
                            field: 'current_price', 
                            type: ['internalPrice'], // internalPrice sets widths, editable, cellClass, valueParser, valueFormatter
                            headerTooltip: 'Current Price - NOT SHOWN TO VENDORS',
                            // hide: true,  // TURNING OFF INTERNAL PRICES FOR NOW
                            hide: !this.quoteSheetData.internalCols.show_current_price,
                            cellClass: ['column-align-right'],
                            minWidth: 80, 
                            maxWidth: 150,
                            flex: 1,
                            valueFormatter: (params) => {
                                let rawAmount = toNumberOrNull(params.value);
                                const currencyCode = params.data?.current_price_currency || this.quoteSheetData.currency;  // use the currency on the row if available, otherwise default to quoteSheet currency
                                const decimalPlaces = 4;
                                return hasValueOrZero(rawAmount) ? formatMoney(rawAmount, currencyCode, decimalPlaces) : '';  // formatMoney will return '' if amount is null or undefined
                            },
                            // TODO - figure out how to set amount and currency...
                            valueSetter: this.qsVariantSetter,  // common one OK?
                            valueSetterParser: (valueSetterParams) => {
                                // custom validation/parser method used in valueSetter - return { value (any), isValid (boolean) }
                                // this is only called inside valueSetter -- our generic valueSetter handles putting data into correct structure, this handles parsing/validating the value
                                // why? because in our parsers (esp. for pasting) if value is garbage, cannot be parsed to something meaningful, we have no way to reject it, so we need validator too, which works in valueSetter by returning false there -- this combines these things into same step
                                // params here are same as in valueSetter - so we have access to old and new values { oldValue, newValue, data, node, colDef, column, api, columnApi, context }
                                // MUST be able to pass in null/empty values to UNSET current value, or else it's never possible
                                let isValid = true;
                                let value;
                                // for prices/numbers, we will only accept things that don't return NaN in _.toNumber -- null, '' both return 0 from that, which makes sense, but 'some_string' returns NaN
                                // changed to use @international/number for parsing -- to accomodate european-style numbers (comma as decimal separator)
                                const useLocalizedParsing = false;  // set to true to use localized parsing -- WHEN READY (added March 2025)
                                // TODO - change the tooltip when you ochange to use localized parsing!
                                const rawValue = valueSetterParams.newValue;  // this is the raw value coming in from the editor
                                const parsedValue = useLocalizedParsing ? parseDecimal(rawValue) : toNumber(rawValue);
                                if (isNaN(parsedValue)) {
                                    // if new value cannot be coerced to a number, then ignore it - but DO NOT NECESSARILY USE THE RETURN VALUE FROM toNumber - because '' -> 0 -- but you don't want that... you want '' -> null
                                    isValid = false;
                                    value = null;  // doesn't matter actually
                                } else {
                                    value = useLocalizedParsing ? agNumberFixedDecimalParser(parsedValue, 4) : agNumberFixedDecimalParser(rawValue, 4);
                                    isValid = true;
                                }
                                // originally I thought we should check oldValue for some things... if old value was valid, but new value is ambiguous, then don't change it... but I don't think that's necessary?
                                return { value, isValid };
                            },
                        },
                        {
                            headerName: 'Target Price', 
                            field: 'target_price', 
                            type: ['internalPrice'],  // internalPrice sets widths, editable, cellClass, valueParser, valueFormatter
                            headerTooltip: 'Target Price - NOT SHOWN TO VENDORS',
                            // hide: true,  // TURNING OFF INTERNAL PRICES FOR NOW
                            hide: !this.quoteSheetData.internalCols.show_target_price,
                            cellClass: ['column-align-right'],
                            minWidth: 80, 
                            maxWidth: 150,
                            flex: 1,
                            valueFormatter: (params) => {
                                let rawAmount = toNumberOrNull(params.value);
                                const currencyCode = params.data?.target_price_currency || this.quoteSheetData.currency;  // use the currency on the row if available, otherwise default to quoteSheet currency
                                const decimalPlaces = 4;
                                return hasValueOrZero(rawAmount) ? formatMoney(rawAmount, currencyCode, decimalPlaces) : '';  // formatMoney will return '' if amount is null or undefined
                            },
                            // valueParser: agNumberParser,
                            valueSetter: this.qsVariantSetter,  // common one OK?
                            valueSetterParser: (valueSetterParams) => {
                                // custom validation/parser method used in valueSetter - return { value (any), isValid (boolean) }
                                // this is only called inside valueSetter -- our generic valueSetter handles putting data into correct structure, this handles parsing/validating the value
                                // why? because in our parsers (esp. for pasting) if value is garbage, cannot be parsed to something meaningful, we have no way to reject it, so we need validator too, which works in valueSetter by returning false there -- this combines these things into same step
                                // params here are same as in valueSetter - so we have access to old and new values { oldValue, newValue, data, node, colDef, column, api, columnApi, context }
                                // MUST be able to pass in null/empty values to UNSET current value, or else it's never possible
                                let isValid = true;
                                let value;
                                // for prices/numbers, we will only accept things that don't return NaN in _.toNumber -- null, '' both return 0 from that, which makes sense, but 'some_string' returns NaN
                                // changed to use @international/number for parsing -- to accomodate european-style numbers (comma as decimal separator)
                                const useLocalizedParsing = false;  // set to true to use localized parsing -- WHEN READY (added March 2025)
                                // TODO - change the tooltip when you ochange to use localized parsing!
                                const rawValue = valueSetterParams.newValue;  // this is the raw value coming in from the editor
                                const parsedValue = useLocalizedParsing ? parseDecimal(rawValue) : toNumber(rawValue);
                                if (isNaN(parsedValue)) {
                                    // if new value cannot be coerced to a number, then ignore it - but DO NOT NECESSARILY USE THE RETURN VALUE FROM toNumber - because '' -> 0 -- but you don't want that... you want '' -> null
                                    isValid = false;
                                    value = null;  // doesn't matter actually
                                } else {
                                    value = useLocalizedParsing ? agNumberFixedDecimalParser(parsedValue, 4) : agNumberFixedDecimalParser(rawValue, 4);
                                    isValid = true;
                                }
                                // originally I thought we should check oldValue for some things... if old value was valid, but new value is ambiguous, then don't change it... but I don't think that's necessary?
                                return { value, isValid };
                            },
                        },
                    ],
                },
                {
                    headerName: 'Vendor Response Data',
                    headerGroupComponent: 'titleSubtitleHeaderGroupRenderer',
                    headerGroupComponentParams: {
                        subtitle: 'Select which data vendors must provide for each item, and whether they are allowed to No-Bid a line item.',
                    },
                    marryChildren: true,
                    children: this.buildVendorResponseColDefs(),
                },
            ];
            return newColDefs;
        },
        // WORKING WITH AG-GRID CONTEXT 
        // context is set to this.gridContext with :context="gridContext"
        // we set initial this.gridContext in beforeMount() hook with other gridOptions
        updateGridContext: function() {
            // call this when things change that are inside grid context (this.gridContext)
            // such as currency, other things when you get data from API, and when you change column/quotesheet settings
            //this.gridContext.currency = this.quoteSheetData.currency;
            //this.$nextTick(() => {  // not using nextTick... so I can be more explicit about when this happens
                this.gridContext.currency = this.quoteSheetData.currency;
            //});
            // DO OTHER STUFF TO REFRESH qsItemMap / linkedProductMap???
        },
        addQsItemToContext: function(qsItemObj) {
            // add QuoteSheetItem to the context (this.gridContext.qsItemMap) based on its GUID
            // ONLY USE THIS FROM PLACES WHERE YOU CAN ASSIGN SORT_ORDER & UPDATE SORT_ORDER ON OTHERS
            // SHOULD NOT USE THIS -- USE updateQsItemSortOrder() INSTEAD (which calls this)
            this.gridContext.qsItemMap.set(qsItemObj.guid, qsItemObj);
        },
        removeQsItemFromContext: function(qsItemGuid) {
            // REMOVE a QuoteSheetItem from the grid context (this.gridContext.qsItemMap) based on its GUID
            // AND reindex sort_order on all other qsItems in context to remove the 'hole' from this one
            // RETURN: true if it was in context map and removed, false if it was NOT in context map
            let deleteResult = this.gridContext.qsItemMap.delete(qsItemGuid);
            this.reindexQsItems();
            return deleteResult;
        },
        getQsItem: function(qsItemGuid) {
            // get a QuoteSheetItem class instance from the grid context by its guid
            if (!qsItemGuid) return null;
            return this.gridContext.qsItemMap.get(qsItemGuid);  // will return undefined if not in the Map
        },
        getAllQsItems: function() {
            // get an ARRAY of all QuoteSheetItem instances in the gridContext currently
            return Array.from(this.gridContext.qsItemMap.values());
        },
        addLinkedProductToContext: function(linkedProductObj) {  // linkedProductObj should be dict/obj directly from API
            // add or update a linkedProduct in the context (this.gridContext.linkedProductMap) based on its ID
            // should not have to worry about removing from context when you remove linked qsItems -- since you don't actually send any linked_products back to API -- just their ID, inside qsItem
            //this.gridContext.linkedProductMap.set(linkedProductObj.id, linkedProductObj);
            this.gridContext.linkedProductMap.set(linkedProductObj.id, new LinkedProduct(linkedProductObj));
        },
        getLinkedProduct: function(linkedProductId) {  // returns instance of LinkedProduct (assuming you stored them in context as such)
            return this.gridContext.linkedProductMap.get(linkedProductId);
        },
        qsItemContextGetter: function(params) {
            // only return value if its the first variant in the group
            // changed to use 'invisible' class instead - if (!this.isFirstVariant(params)) return '';
            let fieldKey = params.colDef.field.replace('item_', '');  // colDef 'field' should be 'item_' + name of field in qsItem
            return this.getQsItem(params.data.qs_item_guid)[fieldKey];
        },
        qsItemContextSetter: function(params) {
            const fieldKey = params.colDef.field.replace('item_', '');  // colDef 'field' should be 'item_' + name of field in qsItem
            const setValue = (cleanValue) => {  // local thing for DRY
                let qsItem = this.getQsItem(params.data.qs_item_guid);  // set it in Vue data, then let that get passed to grid via data binding
                qsItem[fieldKey] = cleanValue;
            }
            // using our custom parser in here, because normal valueParser does not work for copy/paste - so we don't use valueParser, use valueSetterParser instead (our custom thing)
            const valueSetterParser = params.colDef.valueSetterParser;  // might not have one!
            if (valueSetterParser) {
                const { value, isValid } = valueSetterParser(params);  // it wants all params, not just the value
                if (isValid) {
                    setValue(value);
                    return true;  // return true to say we did the update, so refresh stuff happens
                } else {
                    console.log('Ignoring invalid value in setter:', params.newValue);
                    return false;
                }
            }
            // no valueSetterParser?  then set value with original incoming value
            setValue(params.newValue);
            return true;  // return true to say we did the update, so refresh stuff happens
        },
        // qsVariantGetter(params) {  // NOT NEEDED
        //     // variants are top-level in data, so we can just get/set using 'field'
        //     let fieldKey = params.colDef.field;
        //     return params.data[fieldKey];
        // },
        qsVariantSetter(params) {
            // variants are top-level in data, so we can just get/set using 'field'
            //      we have a custom setter in order to use a common value parser -- because the normal valueParser does not handle PASTE operations
            const field = params.colDef.field;
            console.log(`from qsVariantSetter in field ${field} - incoming params:`, params);
            const setValue = (cleanValue) => {
                console.log(`setting params.data field ${field} to`, cleanValue);
                params.data[field] = cleanValue;
            };
            const valueSetterParser = params.colDef.valueSetterParser;  // might not have one!
            if (valueSetterParser) {
                console.log('we HAVE valueSetterParser');
                const { value, isValid } = valueSetterParser(params); // it wants all params, not just the value
                if (isValid) {
                    console.log('value IS VALID after valueSetterParser', value);
                    setValue(value);
                    return true;  // return true to say we did the update, so refresh stuff happens
                } else {
                    console.log('Ignoring invalid value in setter and not setting value:', params.newValue);
                    return false;
                }
            }
            // if no valueSetterParser function for this col, then do it the normal way
            setValue(params.newValue);
            return true;  // return true to say we did the update, so refresh stuff happens
        },
        qsVariantSetterSelect(params, choices) {
            // pass in normal valueSetter params AND an array of valid choices
            // no need for valueSetterParser here, since we only allow the choices passed in 
            let choicesArray = (!!choices && Array.isArray(choices)) ? choices : [];
            if (choicesArray.includes(params.newValue)) {
                const fieldKey = params.colDef.field;
                params.data[fieldKey] = params.newValue;
                return true;
            } else {
                console.log('Ignoring invalid value in select setter:', params.newValue);
                return false;
            }
        },
        updateQsItemSortOrder: function({qsItemGuid, newQsItemObj, newSortOrder, skipRefresh = false}) {
            // assign new sort_order for an EXISTING qsItemGuid (ALREADY IN gridContext) and update sort_order for all other qsItems in context too
            // OR... add a NEW QuoteSheetItem to the context at specified newSortOrder -- if newSortOrder not provided, then we'll add to the end
            // ARGS:
            // - qsItemGuid (optional UUID) -- if updating sort_order for EXISTING qsItem already in context, like during drag-n-drop
            // - newQsItemObj (optional QuoteSheetItem instance) -- if inserting a NEW qsItem 
            // - newSortOrder (int, optional, 1-based) -- specified new sort_order for new or existing qsItem -- if not provided we put it at the end
            // - skipRefresh (boolean, default false) -- should we skip calling gridApi.refreshClientSideRowModel('group') at the end?  ONLY skip it if you know you'll do it later
            // newSortOrder should be the sort_order for the index this one should take place of (may not be the same sort order when we're done, based on whether we were going up or down)
            // this will also update all other qsItems in the context too... 
            // CHECK VALID ARGS FIRST
            if ((!!qsItemGuid && !!newQsItemObj) || (qsItemGuid && newQsItemObj)) {
                console.error('Must provide only ONE of qsItemGuid OR newQsItemObj to updateQsItemSortOrder, not both');
                return;
            }
            // get our real sort_order, in case we didn't provide it
            newSortOrder = newSortOrder || (this.gridContext.qsItemMap.size) + 1;  // add it at end if it was not defined...  this will work whether we're adding a NEW variant, or updating position of existing variant, since we 'restack' the sort_order for entire array at the end of this function
            if (newQsItemObj) {  // if adding a NEW one, insert it into context first
                newQsItemObj.sort_order = newSortOrder;  // assign the sort_order here, just so you have some numeric value... but WE MUST STILL update it again AFTER incrementing it for all above it
                this.addQsItemToContext(newQsItemObj);
                qsItemGuid = newQsItemObj.guid;  // set this as qsItemGuid so we can keep everything consistent between new/updates from here on
            }
            let thisQsItem = this.getQsItem(qsItemGuid);
            // increment all qsItems ABOVE the newSortOrder, update sort_order on this one, then make sorted list and update sort_order on ALL of them based on that index, which will remove any 'holes'
            this.gridContext.qsItemMap.forEach((otherQsItem) => {  // otherQsItem (first arg) is the VALUE in qsItemMap 
                // thisQsItem will be in here too... so make sure it has some value for .sort_order already, but maybe just a low number (like 0 or -1)
                if (otherQsItem.sort_order >= newSortOrder) {
                    otherQsItem.incrementSortOrder();
                }
            });
            // UPDATE SORT_ORDER ON ONE YOU'RE WORKING WITH - must be AFTER looping through all of them, otherwise it would have gotten incremented  
            // (you COULD do it in the loop above, but then you're adding extra if to each loop iteration, so don't)
            thisQsItem.sort_order = newSortOrder;
            // now re-sort/re-index all QuoteSheetItems in gridContext to clean-up sort_order and remove any 'hole'
            this.reindexQsItems(); // should be safe for updates or insert new, since new one is already in there
            if (!skipRefresh) {
                // should be done, everything was mutated in place in gridContext -- now refresh data starting from 'group'
                this.gridApi.refreshClientSideRowModel('group');  // could be group -> filter -> pivot -> aggregate -> sort -> map
            }
        },
        reindexQsItems: function() {
            // 'restack' / 'reindex' the sort_order for all QuoteSheetItem intances in gridContext
            // this is a utility to be called from other methods
            // don't put any grid refreshing or transactions in here... do that from the caller
            // make a new sorted array, re-assign new sort_order to all to remove 'hole'
            // NO ARGS / NO RETURN - this MUTATES QuoteSheetItem instances in gridContext, in place
            let sortedQsItems = sortBy(Array.from(this.gridContext.qsItemMap.values()), ['sort_order']);  // existing items, sorted by sort_order, returns new array (but underlying objects should still be same)
            sortedQsItems.forEach((qsi, idx) => {
                qsi.sort_order = idx + 1;  // set a new sort_order
            });
        },
        updateVariantSortOrder: function(variantGuid, newSortOrder, newVariantObj) {
            // utility to move a row (by variant guid, not node or data object) to a new place in a group
            // OR, to add a new variant to an EXISTING group, with newSortOrder, updating all sibling sort_order at the same time in one transaction
            // ARGS:
            //  - variantGuid (string (uuid) - optional, required if not providing newVariantObj) -- if only updating the sort_order of an existing row in the grid (like for drag-n-drop)
            //  - newSortOrder (int - optional, if not provided, we'll put at end of siblings) -- 1-based sort_order position 
            //  - newVariantObj (QuoteSheetItemVariant object (not a rowNode) optional, required if not providing variantGuid) - if adding a NEW variant to the group, not updating existing one
            // THIS DOES applyTransaction(update) on the grid internally here -- and also refreshes data!
            // assigns a new sort_order to it, and updates sort_order for all other siblings in the same group
            // used for drag and drop, and inserting new variants
            // STEP 0) Make sure we didn't pass in both variantGuid AND newVariantObj, and 
            // also, set a boolean for whether we're UPDATING EXISTING variant already in the grid, or ADDING NEW variant (newVariantObj)
            if ((!!variantGuid && !!newVariantObj) || (variantGuid && newVariantObj)) {
                console.error('Must provide only ONE of variantGuid OR newVariantObj to updateVariantSortOrder, not both');
                return;
            }
            let affectedRowNodes = [];  // we'll stash all rowNodes here that we have to redraw at the end
            let isUpdate = !!variantGuid;
            // STEP 1) Get the siblings for the variant we're working with
            let allSiblingNodes = [];  // declare the variable with default.... we'll determine this differently based on existing/new variant cases
            if (isUpdate) {  // if updating an EXISTING variant in the grid - the grid must already have a rowNode for it
                let variantNode = this.gridApi.getRowNode(variantGuid);  // we use the variant guid as the node ID
                allSiblingNodes = variantNode.parent.childrenAfterSort;  // will include self
            } else if (newVariantObj) {  // if adding a NEW variant to existing group, find another variant with same qs_item_guid, then get siblings that way
                let existingSiblings = [];
                this.gridApi.forEachNode(existingNode => {  // this seems to be only way to 'find' an existing rowNode -- cannot break early
                    if (existingNode.group === false && existingNode.data.qs_item_guid === newVariantObj.qs_item_guid) {  // make sure to skip any dummy group nodes
                        existingSiblings.push(existingNode);  // we're adding to array instead of just assigning to value, so we don't accidentally mutate things as we loop through
                    }
                });
                // now get allSiblingNodes, instead of just using existingSiblings, we'll get first one, go up to parent.childrenAfterSort... for consistency, and I feel that's better than just using all existingSiblings 
                if (existingSiblings.length) {
                    allSiblingNodes = existingSiblings[0].parent.childrenAfterSort;
                }
            }
            // STEP 2) Do updates & applyTransaction
            // now make sure we have allSiblingNodes -- if newVariantObj is totally new, an only child, then allSiblingNodes will be empty
            if (!allSiblingNodes.length && newVariantObj) {  
                // we'll be here if adding a NEW variant, no existing siblings -- assign sort_order and add to grid
                // you MAY be able to get rid of this, and just let the rest of function proceed... 
                console.log('Adding a newVariantObj, but NO SIBLING NODES');
                newVariantObj.sort_order = 1;  // just assign sort_order of 1, no matter what we passed in for newSortOrder
                this.addRowsToGrid({newDataRows: [newVariantObj]})  // this does all needed refresh, etc.
                return;  // done... skip the rest
            }
            if (!allSiblingNodes.length && !newVariantObj) {
                // if we did NOT have existing siblings and also do NOT have newVariantObj, something is wrong
                console.error('Trying to update Variant sort_order, but no sibling variants found');
            }
            // NOW, IF WE HAVE SIBLINGS, NEED TO DEAL WITH SORT_ORDER BEFORE APPLY TRANSACTION
            let txnData = {}; // initialize the object we'll pass to applyTransaction --- add 'add' and 'update' keys as we go
            // increment the sort_order for everything ABOVE newSortOrder, then assign new sort_order on this variant, then 'restack' them to remove the hole
            // but first... make sure newSortOrder is not undefined (since its an optional arg)
            newSortOrder = newSortOrder || (allSiblingNodes.length) + 1;  // add it at end if it was not defined...  this will work whether we're adding a NEW variant, or updating position of existing variant, since we 'restack' the sort_order for entire array at the end of this function
            // if we have a NEW variant (newVariantObj) we can deal with that first, before dealing with siblings
            if (newVariantObj) {  // assign the newSortOrder since its not in allSiblingNodes array, and add to txnData object
                newVariantObj.sort_order = newSortOrder;
                txnData['add'] = [newVariantObj];
            }
            // NOW DEAL WITH SIBLING UPDATES (if updating existing variant, it will be in here too)
            // make a new array of the updated DATA (not nodes) -- then use that in applyTransaction when done modifying things
            let updatedSiblingData = allSiblingNodes.map(sibNode => {
                let snData = sibNode.data;  // make a copy?  DO NOT just use spread syntax... it won't clone class instance, so your class methods will be GONE (like .isValid())
                if (snData.sort_order >= newSortOrder) {  // if sort_order is above where we're moving this variant, increment it
                    snData.sort_order++;
                }
                if (variantGuid && snData.guid === variantGuid) {  // if updating an existing variant, and its the one being updated, we can assign new sort_order in here
                    snData.sort_order = newSortOrder;
                }
                return snData;  // this goes into the new array (result of map)
            });  // we have new sort_orders on all EXISTING variants, but MAYBE still a 'hole' for the old sort_order for the one we moved, if updating existing
            let sortedSiblingData = [...updatedSiblingData];  // existing items, sorted by sort_order, returns new array (but same underlying data objects)
            if (isUpdate) {
                // now actually 'RESTACK' all the siblings, but ONLY IF WE UPDATED AN EXISTING ONE
                // if adding a NEW variant, there will NOT be a 'hole' and restacking them will un-increment the increment you just did, so you don't need to to this
                sortedSiblingData = this.reindexVariantsInGroup(updatedSiblingData);  // this sets a new sort_order for each one to remove the 'hole'
            }
            // we now have sortedSiblingData -- add to txnData for update
            if (sortedSiblingData.length) {
                txnData['update'] = sortedSiblingData;
            }
            let txnResult = this.gridApi.applyTransaction(txnData);
            //console.log(`Just updated all sort_order for ${sortedSiblingData.length} variant(s)${newVariantObj ? ' and added newVariantObj' : ''} - applyTransaction result below`);
            //console.log(txnResult);
            // REFRESH DATA??? -- should be no need after applyTransaction(), however, some rowClasses were not getting updated, so we'll call redrawRows on all affected rows
            //this.gridApi.refreshClientSideRowModel('sort');  // could be group -> filter -> pivot -> aggregate -> sort -> map
            // get all the rowNodes we need to redraw
            if (txnResult) {
                if (txnResult.add && txnResult.add.length) {
                    txnResult.add.forEach(addedRowNode => {
                        affectedRowNodes.push(addedRowNode);
                    });
                }
                if (txnResult.update && txnResult.update.length) {
                    txnResult.update.forEach(updatedRowNode => {
                        affectedRowNodes.push(updatedRowNode);
                    });
                }
            }
            // IF WE ADDED newVariantObj, then set focus on that new one
            // if its the ONLY variant, set focus on 'item_name' colKey, if has siblings set focus on 'description' colKey
            if (newVariantObj) {
                let addedNode = txnResult.add[0];
                if (addedNode) {
                    let focusColKey = (addedNode.parent.allChildrenCount === 1) ? 'item_name' : 'description';
                    this.setFocusedCell({rowNode: addedNode, colKey: focusColKey});
                    affectedRowNodes.push(addedNode);
                }
            }
            // now finally redraw all affected rows 
            if (affectedRowNodes.length) {
                this.gridApi.redrawRows({rowNodes: affectedRowNodes});
            }
        },
        reindexVariantsInGroup: function(variantDataArray) {  // takes row DATA, not rowNodes
            // 'restack' or 'reindex' the sort_order for all variants in a group -- pass in an ARRAY of QuoteSheetItemVariant objects (row DATA, not rowNodes)
            // called from multiple places, to remove a 'hole' in the sort_order after moving or deleting a variant
            // don't put any grid refreshing or transactions in here... do that from the caller
            // this will sort all variants by their current 'sort_order' first, then assign new sort_order in that same order to remove any 'holes'
            // RETURNS NEW ARRAY (of the same objects, but objects are mutated)
            let sortedVariants = sortBy(variantDataArray, ['sort_order']);  // existing items, sorted by sort_order, returns new array (but same underlying data objects)
            sortedVariants.forEach((variant, idx) => {  // run through all and assign another new sort_order again to remove the 'hole'
                variant.sort_order = idx + 1;  // set a new sort_order
            });
            return sortedVariants;
        },

        // API METHODS
        showLoadingMessage: function(msg) {
            // overwrite entire loadingOverlayComponentParams object, doing in function will make it react right away (vs. computed prop)
            this.loadingOverlayComponentParams = {loadingMessage: msg}; 
            this.$nextTick(() => this.gridApi.showLoadingOverlay());
        },
        getData: function() {
            // get the data from API service
            this.loading = true;
            this.showLoadingMessage('Loading...');
            console.log(`About to make API call getData for quoteSheetId: ${this.quoteSheetId}`);
            return suppliosApi.quoteSheet.retrieve(this.quoteSheetId)
                .then(response => {
                    this.setLocalData(response.data);
                    return true;
                })
                .catch(error => {
                    this.apiErrorHandler(error, 'There was an error getting the Quote Sheet data.  Please contact Support.');
                    return false;
                })
                .then(success => {
                    this.loading = false;
                    this.gridApi.hideOverlay();
                    // RUN VALIDATION IN CASE SAVED DATA IS NO LONGER VALID
                    // HOLD FOR NOWthis.validateAll(); 
                    return success;
                });
        },
        setLocalData: function(respData) {  // NEW
            // building variant lines, and items/products to grid context
            console.log('hello from the top of setLocalData');
            this.hasSubmittedReplies = respData.has_submitted_replies;  // used to determine this.isEditAllowed
            this.quoteSheetData = new QuoteSheet(respData);
            this.setIsEditAllowed();
            // NEW Aug 9 2022 - set this.usePnForName based on 'show_name' being on/off
            this.usePnForName = this.quoteSheetData?.internalCols?.show_name === false;  // check if EXPLICITLY false
            // build the grid lines (variants) and items/products to context
            this.setGridLines(respData.quotesheet_items);
            // updateColDefs -- must do this AFTER setting the grid data -- so we know if we have variants, etc.
            //this.$nextTick(() => this.updateColDefs());
            this.updateColDefs();
            // then finally force refresh of data in case we had isEditAllowed = false -- or redraw?
            //this.forceGridRefresh(false);
            //this.redrawAllRows();
            // CLEAN / MAKE THINGS NICE FOR THE GRID
            // HOLD FOR NOW this.setRowCurrency(this.quoteSheetData.currency);  // make sure all rows have the quoteSheetData currency 
            // RESET ROW HEIGHTS AND COL WIDTHS
            // this.gridApi.expandAll();  // just to be sure
            // -- already did this in updateColDefs -- this.gridApi.sizeColumnsToFit();  // this will then trigger update row heights via @event callback
            //console.log('Here is what we just set as rowData');
            // -- already in updateColDefs -- this.validateAll();
            // FINAL REDRAW AFTER VALIDATION -- make sure you don't do any/many redraw/refresh before this!
            // Not needed after updateColDefs? - this.redrawAllRows(); // maybe not needed if setColumnDefs() (inside updateColDefs) does the full redraw already
        },
        setGridLines: function(qsItemsFromApi) {  // NEW
            // take all the nested quotesheet_items from API, make grid lines (variants) and build context
            // set gridContext, hasVariants, and set local grid data here also
            //console.log('hello from the top of setGridLines - here are qsItemsFromApi');
            //console.log(qsItemsFromApi);

            // NEW - Aug 9 2022 - if this is a NEW QuoteSheet (ie: no qsItemsFromApi yet) - then we will add blank rows
            // qty of blank rows is tenant-customizable, use this.blankRowCount which returns that or default
            // how? check if has any items, but still execute everything as normal, to make sure all needed things are setup
            // then at the end, if no incoming items from api, then create blank rows
            const hasNoItems = qsItemsFromApi.length === 0;

            let variantLines = [];  // put QuoteSheetItemVariant class instances here, then we'll set as rowData
            let qsItemMap = new Map();  // put QuoteSheetItem class instances here, then we'll add to context
            let linkedProductMap = new Map();  // put LinkedProduct class instances here, then we'll add to context
            let quoteSheetId = this.quoteSheetId;  // TODO - remove this, add it to serializer instead
            const defaultCurrency = this.quoteSheetData?.currency || '';  // just in case
            qsItemsFromApi.forEach(item => {
                let qsItemData = {...item, quotesheet_id: quoteSheetId, use_pn_for_name: this.usePnForName};  // we set usePnForName as top-level thing
                //console.log('Here is the data we use to instantiate new QuoteSheetItem');
                //console.log(qsItemData);
                qsItemMap.set(qsItemData.guid, new QuoteSheetItem(qsItemData));
                if (item.linked_product) {
                    linkedProductMap.set(item.linked_product.id, new LinkedProduct(item.linked_product));  // TODO -- make sure you have all the needed fields?
                }
                item.variants.forEach(variant => {
                    let variantData = {default_currency: defaultCurrency, ...variant, qs_item_guid: item.guid};
                    variantLines.push(new QuoteSheetItemVariant(variantData));
                });
            });
            // now set context maps, and set rowData that you just built!
            this.gridContext.qsItemMap = qsItemMap;
            this.gridContext.linkedProductMap = linkedProductMap;
            // determine if we have multiple variants, set this.hasVariants to allow showing variant description column
            // don't use this here, since it does all updates/refreshes too -- this.updateHasVariants(variantLines);
            this.hasVariants = !this.checkUnique(variantLines.map(vl => vl.qs_item_guid));
            // call anything to make sure grid knows about new context???  seems to work ok without it
            this.gridApi.setRowData(variantLines);
            //refresh? // HOLD OFF -- do a big refresh at the end, in caller?
            // DONE -- no need to return anything...?
            // NEW PART - Aug 9 2022 - if we didn't have any rows, we'll add blank ones
            if (hasNoItems) {
                const rowQty = this.blankRowCount;
                console.log(`NO EXISTING ITEMS - so adding ${rowQty} blank rows`);
                this.addBlankRow(rowQty);
            }
        },
        // SENDING BACK TO API
        getQsItemOverrideObj: function() {  // we are doing most of this on backend also... redundant?
            // return an 'override object' to use in .formatForApi(overrideObj) for QuoteSheetItem
            // this is based on what columns are turned on/off, and the 'disabledValues' object of QuoteSheetItem
            let disabledQsItemCols = [];  // start with blank array
            if (this.quoteSheetData.internalCols && this.quoteSheetData.internalCols.show_part_number === false) {  // explicitly check if false, not just falsey
                disabledQsItemCols.push('part_number');
            }
            if (this.quoteSheetData.internalCols && this.quoteSheetData.internalCols.show_description === false) {
                disabledQsItemCols.push('description');
            }
            let qsItemDisabledValues = QuoteSheetItem.disabledValues();
            let overrideObj = {};  // return this
            disabledQsItemCols.forEach(discol => {
                if (Object.keys(qsItemDisabledValues).includes(discol)) {  // check if we have a value for it first
                    overrideObj[discol] = qsItemDisabledValues[discol];
                }
            });
            //console.log(`The QsItem overrideObj is:`);
            //console.log(overrideObj);
            return overrideObj;
        },
        getVariantOverrideObj: function() {  // we are doing most of this on backend also (except blanking variant description)
            // return an 'override object' to use in .formatForApi(overrideObj) for QuoteSheetItemVariant
            // this is based on what columns are turned on/off, and the 'disabledValues' object of QuoteSheetItemVariant
            let disabledVariantCols = [];  // start with blank array
            // LOOPING THROUGH VENDOR COLS:  must use this.vendorColsAll for the loop... NOT Object.entries(this.quoteSheetData.vendorCols) -- because you may have extra stuff in there such as choice lists for payterms, shipterms, etc.
            this.vendorColsAll.forEach(vcol => {  // vcol.enableKey will be things like 'enable_price', 'enable_moq', etc...
                // now look at this.quoteSheetData.vendorCols[vcol.enableKey] to get boolean value of enabled or not enabled (will be real booleans, not strings)
                let vcolEnabled = this.quoteSheetData.vendorCols[vcol.enableKey];  // returns a real boolean (true/false, not on/off)
                if (vcolEnabled === false) {  // check specifically for false, not just falsey
                    disabledVariantCols.push(vcol.enableKey);  // add things like 'enable_price', 'enable_moq' to our list of disabled vendor cols
                }
            });
            // NOW GET OTHER VARIANT COLS THAT ARE NOT VENDOR COLS (internalCols)
            if (this.quoteSheetData.internalCols && this.quoteSheetData.internalCols.show_need_by_date === false) {  // explicitly check if false, not just falsey
                disabledVariantCols.push('need_by_date');
            }
            if (this.quoteSheetData.internalCols && this.quoteSheetData.internalCols.show_quantity === false) {  // explicitly check if false, not just falsey
                disabledVariantCols.push('quantity');
            }
            if (this.quoteSheetData.internalCols && this.quoteSheetData.internalCols.show_uom === false) {
                disabledVariantCols.push('uom');
            }
            if (this.quoteSheetData.internalCols && this.quoteSheetData.internalCols.show_timeframe === false) {
                disabledVariantCols.push('quantity_timeframe');
            }
            // TODO - also add target_price / current_price?  yes... if you want logic to be consistent....
            // although I'm not sure if all of this is needed / wanted.... do we really need to blank all these things?
            // since we only call this right before we save, and not just when we toggle stuff on/off, its not bad
            // but maybe we do want to preserve these things?  so they can be entered, but just not shown?
            // for now we'll stick with our original logic... blank them out if column is off -- can change behavior for all cols later if needed
            // NOTE: we do not blank the currency fields if we blank the price fields... backend will handle that, and otherwise we'd have to re-set it here on front-end if re-enabled later and page is not reloaded, right?
            if (this.quoteSheetData.internalCols && this.quoteSheetData.internalCols.show_current_price === false) {
                disabledVariantCols.push('current_price');
                // disabledVariantCols.push('current_price_currency');
            }
            if (this.quoteSheetData.internalCols && this.quoteSheetData.internalCols.show_target_price === false) {
                disabledVariantCols.push('target_price');
                // disabledVariantCols.push('target_price_currency');
            }
            if (this.hasVariants === false) {
                disabledVariantCols.push('description');  // if no variants, then blank out the variant description for all 
            }
            let variantDisabledValues = QuoteSheetItemVariant.disabledValues();
            let overrideObj = {};  // return this
            disabledVariantCols.forEach(discol => {
                if (Object.keys(variantDisabledValues).includes(discol)) {  // check if we have a value for it first
                    overrideObj[discol] = variantDisabledValues[discol];
                }
            });
            //console.log(`The VARIANT overrideObj is:`);
            //console.log(overrideObj);
            return overrideObj;
        },
        prepareDataForApi: function() {  // format data to send to backend after editing - return the object
            // API is expecting top level fields:  all QuoteSheet fields, 'qs_items' (list/array of QuoteSheetItem instances without nested variants), 'variants' (list/array of QuoteSheetItemVariant instances)
            let qsItemMap = new Map();  // ANOTHER local Map we'll use to hold the QuoteSheetItem instances we actually want to send to API -- using a Map to make sure we avoid duplicates, to track insertion order, and because we can do quick .has() checks to see if its already there
            // this is important because we may have extra QuoteSheetItems in the context when we delete its variants, so must get qsItems only connected to variants
            let variants = [];  // fill this up, will get sent in POST
            let apiData = this.quoteSheetData.formatForApi();  // quoteSheetData.formatForApi() just puts in the top-level stuff, not nested lines - should be a QuoteSheet class instance
            // NEW 
            // get overrideObj to use for qsItems and variants 
            let qsItemOverrideObj = this.getQsItemOverrideObj();
            let variantOverrideObj = this.getVariantOverrideObj();
            // get variants from grid rows, AND qs_items... because this is how we know the ORDER of qs_items in the grid
            this.gridApi.forEachNode((node) => {  
                // run through each node, check if its a real row (not a group row)
                // get the qs_item from context, format it for API (with correct sort order), add to array, keep track of which qs_items you've already added to array
                // then add the variant itself to that array -- call node.data.formatForApi() -- no longer setting sort_order in here
                // NEW Aug 6 2022 - before all that, check if its an empty row
                if (node.group === false && !this.isRowEmpty({rowNode: node})) {  // make sure its not a group node, check for explicit false (instead of other falsy values)
                    // careful about ordering in if()... because isRowEmpty returns null if you call it on a group node
                    let qs_item_guid = node.data.qs_item_guid;
                    if (!qsItemMap.has(qs_item_guid)) {  // if we didn't already add this QuoteSheetItem to the map, then get it from gridContext and add it
                        let qsItem = this.getQsItem(qs_item_guid); // get the item, then we still need to formatForApi()
                        qsItemMap.set(qs_item_guid, qsItem.formatForApi(qsItemOverrideObj));
                    }
                    variants.push(node.data.formatForApi(variantOverrideObj));
                }
            });
            // now put variants & qs_items in apiData object -- this is what we'll actually send to API
            apiData['qs_items'] = Array.from(qsItemMap.values());  // make an Array from the Map we built
            apiData['variants'] = variants;
            // POST/PUT apiData object to API
            console.log('Full object ready for API:');
            console.log(apiData);
            return apiData;
        },
        startSave: function() {  // start process of saving data to API - call validate, then if all OK, POST/PUT to API
            if (!this.isEditAllowed) {return}  // return a resolved promise instead?
            this.gridApi.stopEditing();  // make sure we end any in-process edits to make sure all changes are in the grid data
            let currentLastDisplayedIndex = this.gridApi.getLastDisplayedRow();  // so we can use ensureIndexVisible(index, position) after the save to go to same spot
            // put some cleaning step in here to clean things while still in grid, but before you check validation?  ... maybe later
            let allIsValid = this.validateAll();
            if (!allIsValid) {
                this.$toast.warning('Errors found.  Please fix before saving.');
                return;
            }
            this.canSetDirty = false;  // turn off setDirty until everything is loaded again
            this.saving = true;
            this.showLoadingMessage('Saving Quote Sheet...');
            let apiData = this.prepareDataForApi();
            return suppliosApi.quoteSheet.bulkUpdate(this.quoteSheetId, apiData)
                .then(response => {
                    this.$toast.success('Quote sheet successfully saved');
                    console.log('Successfully saved data, full response:');
                    console.log(response);
                    this.setLocalData(response.data);  // NEW
                    // make sure to set this.canSetDirty above this somewhere
                    console.log('will set isDirty false');
                    this.isDirty = false;  // reset this!
                    return true;
                })
                .catch(error => {
                    this.apiErrorHandler(error, 'Error while saving Quote Sheet.  Please contact Support.');
                    return false;
                })
                .then(success => {
                    console.log(`Scrolling to index: ${currentLastDisplayedIndex}`);
                    //? this.gridApi.ensureIndexVisible(currentLastDisplayedIndex, 'bottom');  // scroll to the same position we had before save
                    this.saving = false;
                    this.gridApi.hideOverlay();
                    this.canSetDirty = true; // re-enable this so we're ready to track isDirty again
                    return success;
                });
        },
        saveItemToSourcingDb: function(rowNode) {
            // save a qsItem to the sourcing DB, passing in a rowNode
            // get the qsItem, make sure it doesn't have linked product already, call save via API, 
            // get OK response, add linkedProduct to context, add linked_product_id to qsItem, redraw all rows in the group
            // we won't save qsItem to QuoteSheet yet... do that with the rest of the lines together so it all validates OK
            // this means we may add a product to sourcing DB, but no save on quotesheet yet... that matches what would happen for adding existing saved items, so should be OK
            // FEB 17 2023 - SHOULD CHANGE TO EITHER SAVE NEW OR LINK TO EXISTING USING PART NUMBER! different endpoint for that...
            //              THEN... if it comes back (either saved or existing) use the values for name/description only if there aren't already ones set on the QuoteSheetItem
            let qsItem = this.getQsItem(rowNode.data.qs_item_guid);
            if (!this.isEditAllowed || !qsItem || !qsItem.canCreateLinkedProduct()) {return Promise.resolve(true)}  // return a resolved Promise if you're doing async
            let productData = qsItem.getCreateLinkedProductData();
            //return suppliosApi.item.create(productData)
            // Feb 17 2023 - changed to get_or_create_by_part_number instead of only create - structure is different {item, created} and status code will be 200 (get) or 201 (create)
            //      NOTE - the ITEM description is still editable on quote sheet even after saving item/product (but description IS included in the save of Product)
            //           - that's on purpose, so it is a place to customize things just for this quote sheet 
            return suppliosApi.item.get_or_create_by_part_number(productData)
                .then(response => {
                    const wasNewlyCreated = response.data.created;
                    let newLinkedProductData = response.data.item;
                    // must still add to context, whether we created new, or existing... might already have in context if its on another line... but should be OK to overwrite it
                    this.addLinkedProductToContext(newLinkedProductData);  // this instantiates a new class instance of LinkedProduct
                    if (wasNewlyCreated) {
                        this.$toast.success('Item was saved to sourcing database');
                    } else {
                        this.$toast('Linked with existing item');
                    }
                    qsItem.setLinkedProduct(newLinkedProductData.id);  // will this update OK?  Make sure it matches how your valueSetter works for qsItems - this should blank out any local data on name, description, p/n???
                    // must also specifically tell grid data to refresh?  added that to redrawGroup
                    this.redrawGroup(rowNode);
                    return true;
                })
                .catch(error => {
                    this.apiErrorHandler(error, 'Error while saving this item.');
                    // could be from a conflict with existing P/N -- error message should say that
                    return false;
                })
                .then(success => {
                    // hide overlay?
                    return success;
                })
            // do any other messaging/overlays, etc.?
        },
        clearLinkedProduct(rowNode) {
            // likely called from the action button renderer component - get the qsItem and call its .clearLinkedProduct() method, then redraw the row
            const qsItem = this.getQsItem(rowNode.data.qs_item_guid);
            qsItem.clearLinkedProduct();
            this.redrawGroup(rowNode);
        },
        exportExcel: function() {
            let excelParams = {  // tons of params to customize the export
                sheetName: this.quoteSheetData.title || 'Quote Sheet',
                fileName: 'QuoteSheet_export.xlsx',
                // skipGroups: true, // only works for real groups, not for treeData, you can implement a 'shouldRowBeSkipped()' function to do it manually
            };
            this.gridApi.exportDataAsExcel(excelParams);
        },
    }
}
</script>

<style lang="scss">

@import "../../css/spl-variables.scss";
@import "~ag-grid-community/src/styles/ag-grid.scss";
@import "~ag-grid-community/src/styles/ag-theme-alpine/sass/ag-theme-alpine-mixin";
//@import "../../css/supplios-ag-grid.scss";  // has @mixin ag-supplios-generic-mixin {

@mixin item-group-indicator {   // use @include item-group-indicator;
    content: '';
    position: absolute;
    display: block;
    height: 120%;  // override in each?  make it larger so it always overflows
    width: 4px;
    top: 0;  // override in each for sure
    left: 0;
    background-color: var(--primary-400);
    //background: linear-gradient(to left, var(--gray-100), var(--primary-500)); // works and looks OK, but maybe hard on rendering
    border-top-left-radius: 0;
    border-top-right-radius: 5px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 5px;
}

// AG-Grid things

.ag-theme-alpine-qs-builder {

    @include ag-theme-alpine((
        // PUT THEME PARAMETERS HERE
        //alpine-active-color: deeppink
        background-color: $gray-200,
        odd-row-background-color: null,  // if null, it uses the background-color
        row-hover-color: null,  // add it manually... otherwise its hard to disable only for dragging
        row-border-color: $gray-200,  // I made it match background-color so empty variant cells look normal.   normal value:  ag-derived(secondary-border-color),
        grid-size: 5px,  // normal for alpine seems to be 6px.  its not bad, but should shrink horizontal padding
        // Horizontal padding for grid and header cells (vertical padding is not set explicitly, but inferred from row-height / header-height
        cell-horizontal-padding: ag-derived(grid-size, $times: 2),  // originally was grid-size, $times: 3
 
        // Fonts
        font-family: ("Helvetica Neue", sans-serif),
        font-size: 12px,
    ));

    // INPUTS
    input[class^=ag-] {
        background-color: #fff;
    }

    // ODDS & ENDS in the grid
    .ag-ltr .ag-row-drag {
        margin-right: 5px;  // normally is 10px
    }

    // GROUP HEADERS
    .ag-header-group-cell {
        .ag-header-group-cell-label {
            .ag-header-group-text {  // will apply to all column groups
                font-size: 140%;
            }
            &.title-subtitle-header {  // custom class by us
                .ag-header-group-text {  // heading and subheading are inside this
                    .title-subtitle-header-title {
                        color: var(--primary-700);
                    }
                    .title-subtitle-header-subtitle {
                        font-size: 70%;  // reset this to something more reasonable
                        color: var(--gray-500);
                        margin-top: 3px;
                    }
                }
            }
        }
    }

    // HEADER ROWS / HEADER CELLS
    .ag-header-row {
        .ag-header-cell {
            &.vendor-response-col {
                .ag-header-cell-label {
                    .ag-header-cell-text {
                        white-space: normal;
                        text-align: center;
                    }
                    //justify-content: center;
                }
            }
        }
    }

    // HEADER CELL and normal CELL ALIGNMENT
    .ag-cell,
    .ag-header-cell {
        // you can override these for individual columns
        &.column-align-left,
        &.column-align-left .ag-header-cell-label {
            text-align: left;
            justify-content: flex-start;
        }
        &.column-align-center,
        &.column-align-center .ag-header-cell-label {
            text-align: center;
            justify-content: center;
        }
        &.column-align-right,
        &.column-align-right .ag-header-cell-label {
            text-align: right;
            justify-content: flex-end;
        }
    }

    // ROWS / CELL VALIDATION FORMATTING - put at the bottom to make sure it gets applied
    $invalid-cell-bg-color: var(--danger-100);

    .ag-row {  // most/all of these are applied by rowClassRules
        
        &.row-invalid {
            //background-color: $invalid-cell-bg-color;
            
            &.row-invalid-variant-data {
                .ag-cell.ag-cell-not-inline-editing.response-data-one-required {  // only highlight the vendor response cols where one of them is required
                    background-color: $invalid-cell-bg-color;
                }
            }
        }

        // SHOWING GROUPS / VARIANTS
        &.item-firstrow,
        &.item-firstrow.item-midrow {  // FIRST VARIANT
            //border-top-color: var(--gray-700);
            .ag-cell[col-id=actions] {  // cells have position: absolute by default...
                border: none;
                &::after {
                    @include item-group-indicator;
                    top: 3px;  // shift DOWN
                    height: calc(100% - 6px);  // NEW - if only showing on first row
                    border-bottom-left-radius: 0;
                    //border-bottom-right-radius: 0;
                }
            }
        }
        &.item-midrow {  // MIDDLE VARIANT
            .ag-cell[col-id=actions] {  // cells already have position: absolute 
                border: none;
                &::after {
                    @include item-group-indicator;
                    border-radius: 0;
                }
            }
        }
        &.item-lastrow,
        &.item-lastrow.item-midrow {  // LAST VARIANT  :not(.border-dnd-bottom) // ? 
            // darker border to emphasize item groups
            border-bottom-color: var(--gray-600);
            .ag-cell[col-id=actions] {  // cells already have position: absolute 
                border: none;
                &::after {
                    @include item-group-indicator;
                    top: auto;  // shift UP
                    bottom: 3px;
                    border-top-left-radius: 0;
                    border-top-right-radius: 0;
                }
            }
        }
        &.item-firstrow.item-lastrow {  // ONLY VARIANT
            .ag-cell[col-id=actions] {  // cells already have position: absolute 
                border: none;
                &::after {
                    @include item-group-indicator;
                    top: 3px;
                    height: calc(100% - 6px);
                }
            }
        }

        /* &.item-onlyrow .ag-cell[col-id=description] {
            //background-color: var(--gray-100);  // looks better without different background
        } */

        &.item-multiple-variants {
            &:not(.item-firstrow) {
                .ag-cell[col-id=actions],
                .ag-cell[col-id=item_name],
                .ag-cell[col-id=item_part_number],
                .ag-cell[col-id=item_description] {
                    background-color: var(--gray-100);
                }
            }
            .ag-cell[col-id=description]::after {
                @include item-group-indicator;
                background-color: var(--primary-300);  // slightly different for variants
                top: 3px;
                height: calc(100% - 6px);
            }
        }

        &.item-has-linked-product {
            .ag-cell[col-id=actions],
            .ag-cell[col-id=item_name],
            .ag-cell[col-id=item_part_number] {
                //background-color: var(--gray-100);
                color: var(--gray-700);
            }
        }

        /* &.ag-row-level-0 {
            //border-top-width: 2px;
            .ag-icon.ag-icon-grip {
                font-size: 180%;  // make drag icons bigger for top level rows
            }
        }
        &.ag-row-level-1 {
            .ag-drag-handle.ag-row-drag {
                padding-left: 10px;  // indent the drag icons for variant rows
            }
        } */
        /* &.border-dnd {  // NO LONGER USED
            border-top: 3px solid var(--primary);
            background-color: initial;  // doesn't completely get rid of hover highlight, but close
        } */
        @mixin dnd-border {   // use @include dnd-border;  use in ::after pseudo-elements
            content: '';
            position: absolute;
            display: block;
            height: 3px;  // override in each?  make it larger so it always overflows
            width: 100%;
            top: 0;  // OVERRIDE IN EACH USAGE
            left: 0;
            background-color: var(--primary-400);
            z-index: 1100;
        }
        &.border-dnd-top {
            &::after {
                @include dnd-border;
                height: 3px;
                top: 0;
            }
        }
        &.border-dnd-bottom {
            &::after {
                @include dnd-border;
                height: 4px;  // make it one larger than top, since it will cover bottom border too (1px)
                top: calc(100% - 3px);
            }
        }

        // CELLS
        .ag-cell {
            // must specify things in increasing specificity... remember that later stuff overrides earlier stuff
            background-color: #fff;  // you set overall grid background-color to something darker, then default cells lighter, then modify with more specific selectors
            // GENERAL CELL THINGS
            &:not(.ag-cell-inline-editing) {  // this applies to all cells when NOT inline editing... watch for problems, especially with things like drag handle, etc.
                display: flex;  // trying this out.... to better center text vertically... but may not be great.  normal is display: block
                align-items: center;
                // now try to set the first element within the cell with width: 100% so you can more easily do center/right align
                > *:not(.ag-fill-handle) {  // watch for problems with this...  .ag-fill-handle should have position: absolute, but might depend on other stuff around it
                    width: 100%;
                }
            }
            .grid-placeholder-text {
                color: var(--gray-400);
                font-style: italic;
                font-size: 11px;
            }
            /* .linked-product-value {  // we have a span with this class inside .ag-cell for columns with linked_product values
                color: var(--gray-600);  // use item-has-linked-product on row instead
            } */
            &.ag-cell-wrap-text {
                word-break: break-word; // normally it is break-all
                line-height: normal;
            }
            &.bg-variant-item-cell {
                border-right-color: var(--gray-300);  // variant ITEM cell - make them gray and add a border
                background-color: var(--gray-150);
            }

            // COLUMNS and COLUMN GROUP CELLS
            &.colgroup-quantity {
                background-color: #f5f5f5;
            }
            &[col-id=item_name] {
                font-weight: bold;
            }
        }

        // INVALID ROWS/CELLS -- last so it overrides stuff above
        &.row-invalid {
            .ag-cell[col-id=actions]::after,
            .ag-cell[col-id=description]::after {
                background-color: var(--danger) !important;
            }
            /* .ag-cell,
            .ag-cell[col-id] {
                //background-color: $invalid-cell-bg-color;
            } */
        }
        .cell-invalid {
            background-color: $invalid-cell-bg-color;
        }

        // ROW HOVER -- put this last, so it overrides all cell formatting (if that's what you want)
        /* &.ag-row-hover {
            //background-color: var(--primary-50);
            background-color: ag-param(row-hover-color);  // this gets whatever color you have set for hover in theme params
            .ag-cell,  // doing this for normal cells, and noedit-cell, and any other specific classes you have bg color set for above
            .ag-cell.noedit-cell {
                //background-color: var(--primary-50);
                background-color: ag-param(row-hover-color);
            }
            &.border-dnd-top,
            &.border-dnd-bottom {
                background-color: #fff;
            }
        } */
    }  // end of ag-row section

    .qsheet-variant-buttons {  // what's this???
        opacity: 0.5;
        &:hover {
            opacity: 1;
        }
    }
    /* .ag-row.ag-row-hover {  // doing the cell backgrounds like this (without :not(.ag-row-hover) ) makes the transition smoother
        .ag-cell,
        .ag-cell.quantity-cells,
        .ag-cell.internal-price-cells {
            background-color: var(--primary-100);
        }
    } */
}  // end of custom ag-theme

// NON-GRID STUFF
// ----------------------------------------------------
// Showing column visibility, not in edit mode
.qsheet-col-chip {
    border: 1px solid var(--gray-300);
    border-radius: 20px;
    background-color: var(--gray-100);
    margin-right: 0.3rem;
    margin-bottom: 0.4rem;
    padding: 2px 0.5rem;
    font-size: 80%;
    &.qsheet-col-off {
        //text-decoration: line-through;
        color: var(--gray-500);
    }
    &.qsheet-col-on {
        font-weight: normal;
        border-color: var(--success-100);
        color: var(--success-700);
        background-color: var(--success-50);
    }
}

// Old stuff from non-AG version
$qsheet-border-color: var(--gray-700);

.qsheet-outer {
    border: 1px solid $qsheet-border-color;  
}
.qsheet-line {
    .qsheet-line-component {
        //border-top: 1px solid $qsheet-border-color;
        border-bottom: 1px solid $qsheet-border-color;
    }
    &:last-of-type {
        .qsheet-line-component {
            border-bottom: none;
        }
    }
}

.qsheet-header {
    border-left: 1px solid transparent;  // need this to exactly match width of 'table'
    border-right: 1px solid transparent;  // need this to exactly match width of 'table'
    border-bottom: 1px solid $qsheet-border-color;
    font-size: 12px;
    font-weight: bold;
    position: sticky;
    top: 0;
    z-index: 600;
    .header-group-heading {
        // these are the top spanning headings for various col groups
        width: 100%;
        text-align: center;
        white-space: normal;  // this is same as bootstrap utility class .text-wrap
        font-weight: bold;
        text-transform: uppercase;
        color: var(--gray-500);
        font-size: 12px;
        padding-top: 0.25rem;
        padding-bottom: 0.25rem;
    }
    .item-details-header {
        padding-left: 0.6rem;
        padding-right: 0.6rem;
    }
    .item-details-col {
        background-color: var(--primary-50);
        padding-left: 0.6rem;
        padding-right: 0.6rem;
    }
    .quantity-fields {
        background-color: var(--warning-50);
    }
    .internal-prices {
        background-color: var(--success-50);
    }
    .vendor-cols {
        background-color: var(--gray-150);
    }
}

table.table.qsheet-table {
    td {
        padding: 0;
    }
}

// IMPORTANT - keep these at the bottom so they override other styles you set above
.qsheet-ghost {
    opacity: 0.5 !important;
    //background: #c8ebfb !important;
}
.qsheet-drag {  // class on element being dragged
    border: 1px solid var(--primary);
    border-radius: 0.5rem;
    overflow: hidden;
    .qsheet-line-component {
        border-top: none;
    }
}

</style>